import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { createRoot } from 'react-dom/client';

import MiniBoltIcon from '../../assets/icons/mini-bolt.svg?react';
import MiniBatteryIcon from '../../assets/icons/mini-battery.svg?react';
import MiniTimeIcon from '../../assets/icons/mini-time.svg?react';
import MiniChargerIcon from '../../assets/icons/mini-charger.svg?react';
import ChevronLeftIcon from '../../assets/icons/chevron-left.svg?react';
import ChargerCompModal from '../../components/ChargerCompModal';
import useDashboardRealtimeData from '../../hooks/useDashboardRealtimeData';
import {
  Container as MapDiv,
  NaverMap,
  Marker,
  useNavermaps,
} from "react-naver-maps";
import { useSigunguEupmyeondongStats, useSidoSigunguStats } from '../../hooks/queries/drResourceQueries';
import { useOpmContractNumberInfo } from '../../hooks/queries/resourceRelationQueries';
import { useDRResourcesSimple } from '../../hooks/useDRResourcesSimple';
import useRealtimeScheduler from '../../hooks/useRealtimeScheduler';
import RegionSelector from './components/RegionSelector';

import DRResourceCard from './components/DRResourceCard';
import StationCard from './components/StationCard';
import StationMarker from './components/StationMarker';
import jejuRegionData from '../../assets/jeju_region_en.json';
import Pagination from '../../components/Pagination';
import './MapDashboard.css';
import MiniDashBoardChart from './components/MiniDashBoardChart';

/** 충전기 배열(Status)을 대기/사용/불가 집계 — calculateChargerStats와 동일 규칙 */
const aggregateChargerStatusFromItems = (items) => {
  if (!items?.length) {
    return { total: 0, waiting: 0, using: 0, unavailable: 0 };
  }
  let waiting = 0;
  let using = 0;
  let unavailable = 0;
  items.forEach((charger) => {
    const status = charger.Status ?? 0;
    switch (status) {
      case 2: // 충전대기
      case 6: // 예약중
        waiting++;
        break;
      case 3: // 충전중
        using++;
        break;
      case 0: // 알수없음
      case 1: // 통신이상
      case 4: // 운영중지
      case 5: // 점검중
      case 9: // 상태미확인
      default:
        unavailable++;
        break;
    }
  });
  return {
    total: items.length,
    waiting,
    using,
    unavailable,
  };
};

// 점 형태의 마커 — 호버 시 OpmContractNumberInfo.SecondItems로 카드 표시
const DotMarker = ({ station, markerName, onClick, stats, fallbackChargerStats }) => {
  const [isHovered, setIsHovered] = useState(false);

  const powerContractNumberRaw = useMemo(() => {
    const raw = station?.PowerMeterContractNumber ?? station?.PowerContractNumber ?? '';
    return String(raw).trim();
  }, [station]);

  const drResourceSysId = station?.DrResourceSysId ?? '';

  const opmParams = useMemo(
    () => ({
      DrResourceSysId: drResourceSysId,
      PowerContractNumber: powerContractNumberRaw || undefined,
      fetchEnabled: isHovered && !!powerContractNumberRaw,
    }),
    [drResourceSysId, powerContractNumberRaw, isHovered]
  );

  const { data: opmData } = useOpmContractNumberInfo(opmParams);

  const chargerStats = useMemo(() => {
    if (opmData != null && Array.isArray(opmData.SecondItems)) {
      return aggregateChargerStatusFromItems(opmData.SecondItems);
    }
    return fallbackChargerStats;
  }, [opmData, fallbackChargerStats]);

  const getMarkerColor = () => {
    if (!stats) return '#6B7280';

    const { totalChargers, availableChargers, chargingChargers, unavailableChargers } = stats;

    if (unavailableChargers === totalChargers) {
      return '#EF4444';
    }
    if (chargingChargers > 0) {
      return '#10B981';
    }
    if (availableChargers > 0) {
      return '#3B82F6';
    }
    return '#F59E0B';
  };

  const markerColor = getMarkerColor();
  const title = markerName || station?.CpoStationName || '충전소';

  return (
    <div
      className="dot-marker-container"
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
    >
      <div
        className="dot-marker"
        style={{
          width: '12px',
          height: '12px',
          backgroundColor: markerColor,
          borderRadius: '50%',
          border: '2px solid white',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          position: 'relative',
          zIndex: 1000,
          transform: isHovered ? 'scale(1.3)' : 'scale(1)',
        }}
        title={station?.CpoStationName || '충전소'}
      />

      {isHovered && (
        <div
          role="presentation"
          style={{
            position: 'absolute',
            bottom: '20px',
            left: '50%',
            marginLeft: '-125px',
            zIndex: 1001,
            pointerEvents: 'auto',
            animation: 'fadeInStable 0.2s ease-in-out',
            width: 250,
            cursor: 'pointer',
          }}
          onClick={onClick}
        >
          <div
            style={{
              background: 'white',
              borderRadius: 8,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              padding: 12,
              width: 250,
              display: 'grid',
              gridTemplateColumns: '32px 1fr',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <svg width="16" height="18" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M8.24914 3.61409C8.23656 3.60938 8.22374 3.60524 8.21072 3.60168C8.16929 3.59034 8.12731 3.58527 8.08592 3.58601H6.99996C6.78942 3.58601 6.60874 3.73362 6.56995 3.93731L5.91449 7.37889C5.91219 7.39043 5.91037 7.40203 5.90904 7.41367C5.90028 7.48969 5.91242 7.56443 5.94162 7.63146C5.95011 7.65096 5.96011 7.66997 5.97164 7.68832C5.97735 7.69743 5.98339 7.70628 5.98972 7.71487C6.03842 7.78098 6.10644 7.83387 6.18838 7.86448C6.20116 7.86926 6.21419 7.87346 6.22742 7.87705C6.26864 7.88828 6.31042 7.89329 6.35159 7.89256H7.62961L7.22631 9.96649C7.18611 10.1776 7.31005 10.3856 7.51684 10.454C7.72364 10.5225 7.95007 10.4305 8.04764 10.2384L9.79757 6.79318C9.86537 6.65968 9.85812 6.50114 9.77841 6.37418C9.69869 6.24722 9.55787 6.16994 9.40624 6.16994H8.12043L8.52304 4.09968C8.52539 4.0879 8.52723 4.07605 8.52857 4.06416C8.53717 3.98839 8.52501 3.91392 8.4959 3.84711C8.48742 3.82761 8.47741 3.8086 8.46589 3.79025C8.4606 3.78182 8.45503 3.77361 8.44921 3.76562C8.40041 3.69861 8.33187 3.645 8.24914 3.61409Z"
                  fill="#2563EB"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M1.96875 3.09375C1.96875 1.46278 3.29091 0.140625 4.92188 0.140625H10.8281C12.4591 0.140625 13.7812 1.46278 13.7812 3.09375V15.8906H14.7656C15.3093 15.8906 15.75 16.3313 15.75 16.875C15.75 17.4187 15.3093 17.8594 14.7656 17.8594H0.984375C0.44072 17.8594 0 17.4187 0 16.875C0 16.3313 0.44072 15.8906 0.984375 15.8906H1.96875V3.09375ZM11.8125 3.09375V15.8906H3.9375V3.09375C3.9375 2.55009 4.37822 2.10938 4.92188 2.10938H10.8281C11.3718 2.10938 11.8125 2.55009 11.8125 3.09375Z"
                  fill="#2563EB"
                />
              </svg>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#323B49', lineHeight: 1.2 }}>{title}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <span style={{ fontSize: 10, color: '#6B7280' }}>대기</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#2563EB' }}>{chargerStats.waiting}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <span style={{ fontSize: 10, color: '#6B7280' }}>사용</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#10B981' }}>{chargerStats.using}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <span style={{ fontSize: 10, color: '#6B7280' }}>불가</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#EF4444' }}>{chargerStats.unavailable}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};




// 제주도 경계 좌표
const JEJU_BOUNDS = {
  north: 33.6,
  south: 33.0,
  east: 127.0,
  west: 126.0
};

// 제주도 중심 좌표
const JEJU_CENTER = {
  lat: 33.4,
  lng: 126.5
};

/** 사이드바 충전소 목록 프론트 페이지네이션 (페이지당 개수) */
const STATION_SIDEBAR_PAGE_SIZE = 20;

// 시도/시군구 중심 좌표 및 지역코드
const REGION_CENTERS = {
    '제주도': { lat: 33.4, lng: 126.5, regionCode: '5000000000' },
    '제주시': { lat: 33.5, lng: 126.5, regionCode: '5011000000' },
    '서귀포시': { lat: 33.25, lng: 126.5, regionCode: '5013000000' }
  };

const MapDashboard = () => {
  // 초를 HH:MM:SS 형태로 변환하는 함수
  const formatSecondsToTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '00:00:00';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // 선택된 지역의 regionCode 계산
  const navermaps = useNavermaps();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [map, setMap] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  const [showChargerCompModal, setShowChargerCompModal] = useState(false);
  const [drResourcePage, setDrResourcePage] = useState(1);
  const pageSize = 10;
  const [stationSidebarPage, setStationSidebarPage] = useState(1);
  /** 사이드바에서 강조할 충전소(지도 마커 클릭 등) */
  const [selectedSidebarStationSysId, setSelectedSidebarStationSysId] = useState(null);

  // URL에서 지역 정보 파싱 (메모이제이션 최적화)
  const urlParams = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const params = {
      sido: searchParams.get('sido') || '제주특별자치도',
      sigungu: searchParams.get('sigungu') || '',
      eupmyeondong: searchParams.get('eupmyeondong') || '',
      drResourceId: searchParams.get('drResourceId') || null
    };
    return params;
  }, [location.search]);
  
  // 초기 지도 중심과 줌레벨을 URL 파라미터에 따라 설정
  const initialMapState = useMemo(() => {
    const { sido, sigungu, eupmyeondong } = urlParams;
    
    if (eupmyeondong) {
      const eupmyeondongData = jejuRegionData.find(item => 
        item.RegionSido === sido && 
        item.RegionSigungu === sigungu && 
        item.RegionEupMyuonDong === eupmyeondong
      );
      if (eupmyeondongData) {
        return {
          center: { lat: eupmyeondongData.Latitude, lng: eupmyeondongData.Longitude },
          zoom: 16
        };
      }
    } else if (sigungu) {
      const center = REGION_CENTERS[sigungu];
      if (center) {
        return { center, zoom: 15 };
      }
    } else if (sido) {
      return { center: REGION_CENTERS['제주도'], zoom: 12 };
    }
    
    return { center: JEJU_CENTER, zoom: 10 };
  }, [urlParams.sido, urlParams.sigungu, urlParams.eupmyeondong]); // 개별 속성으로 분리
  
  const [mapCenter, setMapCenter] = useState(initialMapState.center);
  const [zoomLevel, setZoomLevel] = useState(initialMapState.zoom);

  // 지역 선택 상태 (URL을 단일 출처로 사용)
  const selectedRegion = urlParams;
  
  // 미니 대시보드 상태
  const [showMiniDashboard, setShowMiniDashboard] = useState(false);
  const [miniDashboardData, setMiniDashboardData] = useState(null);

  // 사이드바용 DR 자원 쿼리 파라미터 (selectedRegion 기반)
  const sidebarDrResourceParams = useMemo(() => {
    const params = {};
    if (selectedRegion.sido) params.RegionSido = selectedRegion.sido;
    if (selectedRegion.sigungu) params.RegionSiGunGu = selectedRegion.sigungu;
    if (selectedRegion.eupmyeondong) params.RegionEupMyuonDong = selectedRegion.eupmyeondong;
    return params;
  }, [selectedRegion.sido, selectedRegion.sigungu, selectedRegion.eupmyeondong]);
  
  // 사이드바용 DR 자원 데이터 조회 (페이지네이션 포함)
  const sidebarDrResourceQueryParams = {
    ...sidebarDrResourceParams,
    Page: drResourcePage,
    PageSize: pageSize,
  };
  const { 
    drResources: sidebarDrResources, 
    isLoading: sidebarDrResourcesLoading, 
    totalCount: sidebarTotalCount 
  } = useDRResourcesSimple(sidebarDrResourceQueryParams);

  // 클릭된 마커의 지역 정보를 저장하는 상태
  const [clickedRegionInfo, setClickedRegionInfo] = useState(null);
  
  // URL 파라미터 기반 지역 정보 계산
  const urlRegionInfo = useMemo(() => {
    // DR 자원이 선택된 경우 - regionCode를 null로 설정하여 DR 자원 기반 조회가 우선되도록 함
    if (urlParams.drResourceId) {
      return {
        regionCode: null, // DR 자원 ID로 직접 조회하므로 regionCode 불필요
        sido: urlParams.sido,
        sigungu: urlParams.sigungu,
        eupmyeondong: urlParams.eupmyeondong
      };
    }
    
    // 읍면동이 선택된 경우
    if (urlParams.eupmyeondong) {
      const regionData = jejuRegionData.find(data => 
        data.RegionSigungu === urlParams.sigungu && 
        data.RegionEupMyuonDong === urlParams.eupmyeondong
      );
      if (regionData) {
        return {
          regionCode: regionData.RegionCode.toString().slice(0, 8),
          sido: urlParams.sido,
          sigungu: urlParams.sigungu,
          eupmyeondong: urlParams.eupmyeondong
        };
      }
    }
    
    // 시군구가 선택된 경우
    if (urlParams.sigungu && !urlParams.eupmyeondong) {
      return {
        regionCode: null,
        sido: urlParams.sido,
        sigungu: urlParams.sigungu,
        eupmyeondong: null
      };
    }
    
    // 시도만 선택된 경우
    if (urlParams.sido && !urlParams.sigungu) {
      return {
        regionCode: null,
        sido: urlParams.sido,
        sigungu: null,
        eupmyeondong: null
      };
    }
    
    return null;
  }, [urlParams.sido, urlParams.sigungu, urlParams.eupmyeondong, urlParams.drResourceId, jejuRegionData]);
  
  // 실제 사용할 regionInfo: 클릭된 지역 정보가 우선, 없으면 URL 기반 (안정화)
  // drResourceId가 있으면 regionCode를 null로 강제하여 DR 자원 기반 조회가 우선되도록 함
  const regionInfo = useMemo(() => {
    const result = clickedRegionInfo || urlRegionInfo;
    
    // drResourceId가 있으면 regionCode를 null로 강제 (DR 자원 기반 조회 우선)
    if (urlParams.drResourceId && result) {
      return {
        ...result,
        regionCode: null
      };
    }
    
    return result;
  }, [clickedRegionInfo, urlRegionInfo, urlParams.drResourceId]);
  
  // useDashboardRealtimeData를 통해 미니 대시보드용 실시간 데이터 조회
  const dashboardData = useDashboardRealtimeData(regionInfo, urlParams.drResourceId);
  
  // dashboardData의 필요한 속성들을 안정화 (의존성 배열 없이 항상 최신 값 사용)
  const dashboardDataRef = useRef(dashboardData);
  
  // regionInfo가 있는지 확인 (안정화된 값 사용)
  const hasRegionInfo = useMemo(() => {
    return !!(regionInfo || urlParams.drResourceId);
  }, [regionInfo, urlParams.drResourceId]);

  // API 오류 상태를 안정화 (hasError 값만 추적)
  const hasError = useMemo(() => dashboardData.hasError, [dashboardData.hasError]);
  
  // refetchAll 함수를 안정화 (ref로 저장하여 재생성 방지)
  const refetchAllRef = useRef(dashboardData.refetchAll);
  useEffect(() => {
    refetchAllRef.current = dashboardData.refetchAll;
  }, [dashboardData.refetchAll]);

  // 오류 발생 시 스케줄러 비활성화 플래그 (ref로만 관리하여 리렌더링 방지)
  const schedulerStoppedRef = useRef(false);
  const hasErrorRef = useRef(hasError);
  const errorOccurredRef = useRef(false);
  
  // hasError 변경 추적 및 스케줄러 즉시 중지 (리렌더링 방지를 위해 ref만 사용)
  useEffect(() => {
    const prevHasError = hasErrorRef.current;
    hasErrorRef.current = hasError;
    
    if (hasError && !prevHasError) {
      // 에러가 새로 발생한 경우에만 처리 (리렌더링 방지)
      errorOccurredRef.current = true;
      schedulerStoppedRef.current = true;
      console.warn('[MapDashboard] API 오류 감지 - 스케줄러 비활성화');
      // 스케줄러 즉시 중지
      if (stopSchedulerRef.current) {
        stopSchedulerRef.current();
      }
    }
  }, [hasError]); // errorOccurred를 의존성에서 제거하여 리렌더링 방지

  // stopScheduler를 ref로 저장
  const stopSchedulerRef = useRef(null);

  // enabled prop을 안정화 (리렌더링 방지)
  const schedulerEnabled = useMemo(() => hasRegionInfo, [hasRegionInfo]);
  
  // 15분 단위로 실시간 데이터 새로 불러오기 (Dashboard와 동일)
  const {
    currentTime,
    nextUpdateTime,
    lastUpdateTime,
    isUpdating,
    manualUpdate,
    stopScheduler
  } = useRealtimeScheduler(
    useCallback(async (updateTime) => {
      
      // API 오류가 있거나 스케줄러가 중지된 경우 refetch를 건너뛰기
      if (hasErrorRef.current || errorOccurredRef.current || schedulerStoppedRef.current) {
        console.warn('[MapDashboard] API 오류가 발생하여 실시간 업데이트를 건너뜁니다.');
        if (!schedulerStoppedRef.current && stopSchedulerRef.current) {
          stopSchedulerRef.current();
          schedulerStoppedRef.current = true;
        }
        return;
      }
      
      // dashboardData의 refetchAll 함수 호출
      if (refetchAllRef.current) {
        try {
          await refetchAllRef.current();
          // 성공 시 오류 플래그 리셋 (단, hasError가 false인 경우에만)
          if (!hasErrorRef.current) {
            errorOccurredRef.current = false;
            schedulerStoppedRef.current = false;
          }
        } catch (error) {
          console.error('[MapDashboard] 실시간 데이터 업데이트 실패:', error);
          // 에러 발생 시 즉시 플래그 설정 (React Query의 hasError 업데이트 전에)
          errorOccurredRef.current = true;
          schedulerStoppedRef.current = true;
          if (stopSchedulerRef.current) {
            stopSchedulerRef.current();
          }
        }
      }
    }, []), // 의존성 배열을 비워서 완전히 안정화
    {
      enabled: schedulerEnabled, // 안정화된 enabled prop 사용
      immediate: true // 초기 로드 시 즉시 실행
    }
  );
  
  // stopScheduler를 ref에 저장
  useEffect(() => {
    stopSchedulerRef.current = stopScheduler;
  }, [stopScheduler]);
  
  // 디버깅: useRealtimeScheduler 상태 추적
  useEffect(() => {
  }, [hasRegionInfo, isUpdating, hasError]); // errorOccurred 제거 (리렌더링 방지)
  
  // 오류 상태가 해결되면 스케줄러 재시작
  useEffect(() => {
    if (!hasError && errorOccurredRef.current && schedulerStoppedRef.current) {
      // 오류가 해결되었지만 수동으로 재시작해야 함
    }
  }, [hasError]); // errorOccurredRef는 ref이므로 의존성에서 제거
  
  // 실시간 업데이트 정보를 포함한 통합 데이터 (Dashboard.jsx와 동일한 방식으로 단순화)
  const combinedDashboardData = {
    ...dashboardData,
    nextUpdateTime,
    lastUpdateTime,
    isUpdating,
    manualUpdate
  };
  
  // 사이드바 상태 관리
  const [sidebarMode, setSidebarMode] = useState('dr-resources'); // 'dr-resources' | 'stations'
  const [selectedDRResource, setSelectedDRResource] = useState(null);
  const [breadcrumbPath, setBreadcrumbPath] = useState(['DR 자원']);
  
  // 지도 영역 감지 및 통계 API 호출을 위한 상태
  const [visibleRegionCodes, setVisibleRegionCodes] = useState([]);
  const [visibleDRResourceIds, setVisibleDRResourceIds] = useState([]);
  const [selectedMarkerId, setSelectedMarkerId] = useState(null);
  
  // 디바운스를 위한 타이머 참조
  const updateTimerRef = useRef(null);
  /** 마커 클릭 시 충전소 카드로 스크롤 (scrollStationIntoSidebar 정의 후 매 렌더 갱신) */
  const scrollStationIntoSidebarRef = useRef(null);

  // URL 업데이트 함수
  const updateURL = (regionParams, drResourceId = null) => {
    const searchParams = new URLSearchParams();
    if (regionParams.sido) searchParams.set('sido', regionParams.sido);
    if (regionParams.sigungu) searchParams.set('sigungu', regionParams.sigungu);
    if (regionParams.eupmyeondong) searchParams.set('eupmyeondong', regionParams.eupmyeondong);
    if (drResourceId) searchParams.set('drResourceId', drResourceId);
    
    const newSearch = searchParams.toString();
    const newURL = newSearch ? `?${newSearch}` : '';
    navigate(newURL, { replace: false });
  };
  
  
  
  
  
  // DR자원 목록 변경 시 페이지를 1로 초기화
  useEffect(() => {
    setDrResourcePage(1);
  }, [selectedRegion.sido, selectedRegion.sigungu, selectedRegion.eupmyeondong]);
  
  // 페이지 변경 시 스크롤을 상단으로 이동
  const handlePageChange = (newPage) => {
    setDrResourcePage(newPage);
    // 사이드바 스크롤을 상단으로 이동
    const sidebar = document.querySelector('.sidebar-content');
    if (sidebar) {
      sidebar.scrollTop = 0;
    }
  };
  
  // 시군구 선택 시 읍면동 마커들의 통계 정보 조회 (항상 호출)
  const { data: sigunguEupmyeondongStats, isLoading: sigunguEupmyeondongStatsLoading } = useSigunguEupmyeondongStats(
    selectedRegion.sigungu || '제주시' // 기본값으로 제주시 사용
  );
  
 
  
  // 시도 선택 시 시군구 마커들의 통계 정보 조회 (항상 호출)
  const { data: sidoSigunguStats, isLoading: sidoSigunguStatsLoading } = useSidoSigunguStats(
    selectedRegion.sido || '제주특별자치도' // 기본값으로 제주특별자치도 사용
  );
  
  
  // 충전소 상태 데이터는 DR 자원 데이터에 포함되어 있으므로 별도 조회 불필요
  
  // 통계 API 호출 상태 디버깅

  
  // 시군구 목록 (선택된 시도에 따라)
  const sigunguList = useMemo(() => {
    if (!selectedRegion.sido) return [];
    const filtered = jejuRegionData.filter(item => item.RegionSido === selectedRegion.sido);
    const sigunguSet = [...new Set(filtered.map(item => item.RegionSigungu))].sort();
    return sigunguSet;
  }, [selectedRegion.sido]);
  
  // 읍면동 목록 (선택된 시군구에 따라)
  const eupmyeondongList = useMemo(() => {
    if (!selectedRegion.sigungu) return [];
    const filtered = jejuRegionData.filter(item => 
      item.RegionSido === selectedRegion.sido && 
      item.RegionSigungu === selectedRegion.sigungu
    );
    const eupmyeondongSet = [...new Set(filtered.map(item => item.RegionEupMyuonDong))].sort();
    return eupmyeondongSet;
  }, [selectedRegion.sido, selectedRegion.sigungu]);
  
  // 줌레벨에 따른 마커 데이터 계산
  const mapMarkers = useMemo(() => {
    if (!mapLoaded) {
      return [];
    }

    /**
     * DR 자원 선택(URL에 drResourceId) 모드와 그 외를 분리
     * - DR 선택: 줌 15 이상만 충전소 마커, 미만이면 마커 없음(지역 마커 없음)
     * - DR 미선택: 줌에 따라 시도 → 시군구 → 읍면동 → 고줌에서 DR/충전소 목록 마커
     */
    if (urlParams.drResourceId) {
      if (zoomLevel >= 15) {
        const drResourceData = dashboardDataRef.current?.drResourceData?.Items?.[0];
        const stations = drResourceData?.Stations || selectedDRResource?.Stations || [];

        if (stations.length > 0) {
          return stations.map((item) => ({
            id: `station-${item.StationSysId}`,
            name: item.CpoStationName || item.StationName || '충전소',
            type: 'charger',
            lat: item.Lat || item.lat,
            lng: item.Lng || item.lng,
            data: item,
          }));
        }
        return [];
      }
      return [];
    }

    if (zoomLevel <= 10) {
      // 시도 레벨 - 제주도 마커
      const markers = [{
        id: 'jeju',
        name: '제주도',
        type: 'sido',
        lat: REGION_CENTERS['제주도'].lat,
        lng: REGION_CENTERS['제주도'].lng,
        data: { 
          sido: '제주특별자치도',
          regionCode: REGION_CENTERS['제주도'].regionCode
        }
      }];
      return markers;
    } else if (zoomLevel <= 12) {
      // 시군구 레벨 - 제주시, 서귀포시 마커
      const markers = [
        {
          id: 'jeju-si',
          name: '제주시',
          type: 'sigungu',
          lat: REGION_CENTERS['제주시'].lat,
          lng: REGION_CENTERS['제주시'].lng,
          data: { 
            sido: '제주특별자치도', 
            sigungu: '제주시',
            regionCode: REGION_CENTERS['제주시'].regionCode
          }
        },
        {
          id: 'seogwipo-si',
          name: '서귀포시',
          type: 'sigungu',
          lat: REGION_CENTERS['서귀포시'].lat,
          lng: REGION_CENTERS['서귀포시'].lng,
          data: { 
            sido: '제주특별자치도', 
            sigungu: '서귀포시',
            regionCode: REGION_CENTERS['서귀포시'].regionCode
          }
        }
      ];
      return markers;
    } else if (zoomLevel <= 15) {
      // 읍면동 레벨 - jeju_region_en.json 데이터 사용
      const markers = jejuRegionData.map(item => ({
        id: `eupmyeondong-${item.RegionCode}`,
        name: item.RegionEupMyuonDong,
        type: 'eupmyeondong',
        lat: item.Latitude,
        lng: item.Longitude,
        data: {
          sido: item.RegionSido,
          sigungu: item.RegionSigungu,
          eupmyeondong: item.RegionEupMyuonDong,
          regionCode: item.RegionCode
        }
      }));
      return markers;
    } else {
      // 충전소 레벨 - 충전소 데이터가 있으면 충전소 마커, 없으면 DR 자원 마커
      
      // 충전소 데이터가 있으면 충전소 마커 생성 (DR 자원 데이터의 Stations 필드 사용)
      if (selectedDRResource?.Stations?.length > 0) {
        const markers = selectedDRResource.Stations.map(item => {
          return {
            id: `station-${item.StationSysId}`,
            name: item.CpoStationName || item.StationName || '충전소',
            type: 'charger',
            lat: item.Lat || item.lat,
            lng: item.Lng || item.lng,
            data: item
          };
        });
        return markers;
      }
      
      // 충전소 데이터가 없으면 DR 자원 마커 생성
      if (!sidebarDrResources) {
        return [];
      }
      
      const markers = sidebarDrResources.map(item => ({
        id: `dr-${item.DrResourceSysId}`,
        name: item.ResourceName,
        type: 'charger',
        lat: item.lat,
        lng: item.lng,
        data: item
      }));
      return markers;
    }
  }, [zoomLevel, mapLoaded, sidebarDrResources, selectedDRResource, urlParams.drResourceId, urlParams.sido, urlParams.sigungu, urlParams.eupmyeondong]);
  
  // 디바운스된 보이는 마커 업데이트 함수
  const debouncedUpdateVisibleMarkers = () => {
    // 기존 타이머가 있으면 취소
    if (updateTimerRef.current) {
      clearTimeout(updateTimerRef.current);
    }
    
    // 500ms 후에 업데이트 실행
    updateTimerRef.current = setTimeout(() => {
      updateVisibleMarkers();
    }, 500);
  };

  // 보이는 마커들의 지역코드와 DR자원ID를 추출하는 함수
  const updateVisibleMarkers = () => {
    if (!map) {
      return;
    }
    
    // mapLoaded가 false여도 지도가 실제로 준비되었는지 확인
    if (!mapLoaded) {
      try {
        const zoom = map.getZoom();
        if (zoom !== undefined && zoom !== null) {
          setMapLoaded(true);
        } else {
          return;
        }
      } catch (error) {
        return;
      }
    }
    
    const bounds = map.getBounds();
    const currentZoom = map.getZoom();
    
    // 현재 줌레벨에 따라 보이는 마커들 필터링
    // mapMarkers가 비어있다면 실제 줌레벨에 맞는 마커들을 직접 계산
    let markersToCheck = mapMarkers;
    
    if (mapMarkers.length === 0) {
      
      if (currentZoom <= 10) {
        // 시도 레벨 - 제주도 마커
        markersToCheck = [{
          id: 'jeju',
          name: '제주도',
          type: 'sido',
          lat: REGION_CENTERS['제주도'].lat,
          lng: REGION_CENTERS['제주도'].lng,
          data: { 
            sido: '제주특별자치도',
            regionCode: REGION_CENTERS['제주도'].regionCode
          }
        }];
      } else if (currentZoom <= 12) {
        // 시군구 레벨 - 제주시, 서귀포시 마커
        markersToCheck = [
          {
            id: 'jeju-si',
            name: '제주시',
            type: 'sigungu',
            lat: REGION_CENTERS['제주시'].lat,
            lng: REGION_CENTERS['제주시'].lng,
            data: { 
              sido: '제주특별자치도', 
              sigungu: '제주시',
              regionCode: REGION_CENTERS['제주시'].regionCode
            }
          },
          {
            id: 'seogwipo-si',
            name: '서귀포시',
            type: 'sigungu',
            lat: REGION_CENTERS['서귀포시'].lat,
            lng: REGION_CENTERS['서귀포시'].lng,
            data: { 
              sido: '제주특별자치도', 
              sigungu: '서귀포시',
              regionCode: REGION_CENTERS['서귀포시'].regionCode
            }
          }
        ];
      } else if (currentZoom <= 15) {
        // 읍면동 레벨 - jeju_region_en.json 데이터 사용
        markersToCheck = jejuRegionData.map(item => ({
          id: `eupmyeondong-${item.RegionCode}`,
          name: item.RegionEupMyuonDong,
          type: 'eupmyeondong',
          lat: item.lat,
          lng: item.lon,
          data: {
            sido: item.RegionSido,
            sigungu: item.RegionSigungu,
            eupmyeondong: item.RegionEupMyuonDong,
            regionCode: item.RegionCode
          }
        }));
      } else {
        // 충전소 레벨 - DR 자원 데이터 사용
        if (sidebarDrResources) {
          markersToCheck = sidebarDrResources.map(item => ({
            id: `dr-${item.DrResourceSysId}`,
            name: item.ResourceName,
            type: 'charger',
            lat: item.lat,
            lng: item.lng,
            data: item
          }));
        }
      }
      
    }
    
    const visibleMarkers = markersToCheck.filter(marker => {
      const lat = marker.lat;
      const lng = marker.lng;
      
      // 지도 경계 내에 있는지 확인
      let isInBounds = false;
      try {
        // 네이버 지도 API의 bounds 객체 구조에 따라 확인
        if (bounds && typeof bounds.hasPoint === 'function') {
          isInBounds = bounds.hasPoint(new navermaps.LatLng(lat, lng));
        } else if (bounds && bounds.min && bounds.max) {
          // bounds가 min/max 구조인 경우
          isInBounds = lat >= bounds.min.lat() && lat <= bounds.max.lat() && 
                      lng >= bounds.min.lng() && lng <= bounds.max.lng();
        } else if (bounds && bounds.getNorth && bounds.getSouth && bounds.getEast && bounds.getWest) {
          // bounds가 getNorth/getSouth/getEast/getWest 메서드를 가지는 경우
          isInBounds = lat >= bounds.getSouth() && lat <= bounds.getNorth() && 
                      lng >= bounds.getWest() && lng <= bounds.getEast();
        } else {
          // bounds 구조를 모르는 경우 일단 true로 설정 (디버깅용)
          console.warn('bounds 구조를 알 수 없음:', bounds);
          isInBounds = true;
        }
      } catch (error) {
        console.error('bounds 확인 중 오류:', error);
        isInBounds = true; // 오류시 일단 true로 설정
      }
      
      // 줌레벨에 따른 마커 타입 확인
      let shouldShow = false;
      if (currentZoom <= 10 && marker.type === 'sido') shouldShow = isInBounds;
      else if (currentZoom <= 12 && marker.type === 'sigungu') shouldShow = isInBounds;
      else if (currentZoom <= 15 && marker.type === 'eupmyeondong') shouldShow = isInBounds;
      else if (currentZoom > 15 && marker.type === 'charger') shouldShow = isInBounds;
      
      if (shouldShow) {
      }
      
      return shouldShow;
    });
    
    // 지역코드와 DR자원ID 추출
    const regionCodes = [];
    const drResourceIds = [];
    
    visibleMarkers.forEach(marker => {
      if (marker.type === 'sido' && marker.data.regionCode) {
        // 시도 레벨 (제주도)
        regionCodes.push(marker.data.regionCode);
      } else if (marker.type === 'sigungu' && marker.data.regionCode) {
        // 시군구 레벨 (제주시, 서귀포시)
        regionCodes.push(marker.data.regionCode);
      } else if (marker.type === 'eupmyeondong' && marker.data.regionCode) {
        // 읍면동 레벨
        regionCodes.push(marker.data.regionCode);
      } else if (marker.type === 'charger' && marker.data.DrResourceSysId) {
        // DR 자원 레벨
        drResourceIds.push(marker.data.DrResourceSysId);
      }
    });
    
    // 상태 업데이트 (중복 제거)
    const uniqueRegionCodes = [...new Set(regionCodes)];
    const uniqueDRResourceIds = [...new Set(drResourceIds)];
    
    setVisibleRegionCodes(uniqueRegionCodes);
    setVisibleDRResourceIds(uniqueDRResourceIds);
    
  };

  // 지도 이벤트 핸들러
  const handleMapLoad = () => {
    setMapLoaded(true);
    
    // 지도 로드 완료 후 초기 보이는 마커들 업데이트
    setTimeout(() => {
      updateVisibleMarkers();
    }, 100);
  };
  
  const handleMapZoomChanged = () => {
    if (map) {
      const currentZoomLevel = map.getZoom();
      setZoomLevel(currentZoomLevel);
      debouncedUpdateVisibleMarkers();
    }
  };
  
  const handleMapDragEnd = () => {
    if (map) {
      const center = map.getCenter();
      setMapCenter({ lat: center.lat(), lng: center.lng() });
      debouncedUpdateVisibleMarkers();
    }
  };

  // 충전기 상태 계산 함수 (dashboardData에서 DR 자원 데이터를 통해 충전기 정보 가져오기)
  // dashboardDataRef를 사용하여 무한 렌더링 방지
  const calculateChargerStats = useCallback((markerData) => {
    const currentDashboardData = dashboardDataRef.current;
    if (!markerData || !currentDashboardData.drResourceData?.Items) {
      return { total: 0, waiting: 0, using: 0, unavailable: 0 };
    }

    // DR 자원 데이터에서 해당 충전소의 충전기 정보 찾기
    const drResource = currentDashboardData.drResourceData.Items.find(
      item => item.DrResourceSysId === markerData.DrResourceSysId
    );

    if (!drResource || !drResource.Chargers) {
      return { total: 0, waiting: 0, using: 0, unavailable: 0 };
    }

    return aggregateChargerStatusFromItems(drResource.Chargers);
  }, []); // 의존성 배열을 비워서 완전히 안정화 (dashboardDataRef를 통해 최신 값 접근)

  
  // dashboardData 객체 자체는 매번 새로 생성될 수 있으므로, 
  // useEffect 없이 직접 할당하여 무한 렌더링 방지
  dashboardDataRef.current = dashboardData;

  const sidebarStationsAll = useMemo(() => {
    const fromSelected = selectedDRResource?.Stations;
    if (fromSelected?.length) return fromSelected;
    return dashboardData?.drResourceData?.Items?.[0]?.Stations || [];
  }, [
    selectedDRResource?.Stations,
    selectedDRResource?.DrResourceSysId,
    dashboardData?.drResourceData?.Items?.[0]?.Stations,
    dashboardData?.drResourceData?.Items?.[0]?.DrResourceSysId,
  ]);

  const sidebarDrResourceSysIdForCards =
    selectedDRResource?.DrResourceSysId ??
    dashboardData?.drResourceData?.Items?.[0]?.DrResourceSysId ??
    null;

  const sidebarStationsPaginated = useMemo(() => {
    const start = (stationSidebarPage - 1) * STATION_SIDEBAR_PAGE_SIZE;
    return sidebarStationsAll.slice(start, start + STATION_SIDEBAR_PAGE_SIZE);
  }, [sidebarStationsAll, stationSidebarPage]);

  const stationSidebarTotalPages = Math.max(
    1,
    Math.ceil(sidebarStationsAll.length / STATION_SIDEBAR_PAGE_SIZE)
  );

  const sidebarStationsListRef = useRef([]);
  sidebarStationsListRef.current = sidebarStationsAll;

  const scrollStationIntoSidebar = useCallback((stationSysId) => {
    if (!stationSysId) return;
    const list = sidebarStationsListRef.current;
    const idx = list.findIndex((s) => s.StationSysId === stationSysId);
    if (idx < 0) return;
    setStationSidebarPage(Math.floor(idx / STATION_SIDEBAR_PAGE_SIZE) + 1);
    requestAnimationFrame(() => {
      setTimeout(() => {
        document
          .getElementById(`map-sidebar-station-${stationSysId}`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 120);
    });
  }, []);

  scrollStationIntoSidebarRef.current = scrollStationIntoSidebar;

  useEffect(() => {
    setStationSidebarPage(1);
    setSelectedSidebarStationSysId(null);
  }, [sidebarDrResourceSysIdForCards]);

  // sigunguEupmyeondongStats와 sidoSigunguStats를 ref로 추적하여 getMarkerStats 재생성 방지
  const sigunguEupmyeondongStatsRef = useRef(sigunguEupmyeondongStats);
  sigunguEupmyeondongStatsRef.current = sigunguEupmyeondongStats;
  
  const sidoSigunguStatsRef = useRef(sidoSigunguStats);
  sidoSigunguStatsRef.current = sidoSigunguStats;

  // 마커별 통계 데이터 가져오기 (useCallback으로 안정화, 의존성 배열 제거하여 재생성 방지)
  const getMarkerStats = useCallback((marker) => {
    const currentDashboardData = dashboardDataRef.current;
    const currentSigunguEupmyeondongStats = sigunguEupmyeondongStatsRef.current;
    const currentSidoSigunguStats = sidoSigunguStatsRef.current;
    let stats = {
      chargerCount: 0,
      drResourceCount: 0,
      powerDemand: 0
    };

    if (marker.type === 'eupmyeondong' && marker.data.regionCode) {
      // 시군구 선택 시 읍면동 마커들의 통계 정보 사용
      if (currentSigunguEupmyeondongStats?.Items) {
        const regionName = `${marker.data.sigungu} ${marker.data.eupmyeondong}`;
        const regionStat = currentSigunguEupmyeondongStats.Items.find(item => item.RegionName === regionName);
        
        if (regionStat) {
          stats = {
            chargerCount: regionStat.ChargerCount || 0,
            drResourceCount: regionStat.DrResourceCount || 0,
            powerDemand: regionStat.TotalChargerCapacity || 0
          };
        } else {
          // API 데이터가 없을 때 기본값
          stats = {
            chargerCount: 0,
            drResourceCount: 0,
            powerDemand: 0
          };
        }
      } else {
        // API 데이터가 없을 때 기본값
        stats = {
          chargerCount: 0,
          drResourceCount: 0,
          powerDemand: 0
        };
      }
    } else if (marker.type === 'sigungu' && marker.data.regionCode) {
      // 시도 선택 시 시군구 마커들의 통계 정보 사용
      // dashboardData의 제주시/서귀포시 데이터 우선 활용
      const statsData = marker.name === '제주시' ? currentDashboardData.jejuSiData : 
                        marker.name === '서귀포시' ? currentDashboardData.seoguipoSiData : null;
      
      if (statsData?.Items && statsData.Items.length > 0) {
        // dashboardData에서 통계 계산
        const totalStats = statsData.Items.reduce((acc, item) => ({
          chargerCount: acc.chargerCount + (item.ChargerCount || 0),
          drResourceCount: acc.drResourceCount + (item.DrResourceCount || 0),
          powerDemand: acc.powerDemand + (item.TotalChargerCapacity || 0)
        }), { chargerCount: 0, drResourceCount: 0, powerDemand: 0 });
        
        stats = totalStats;
      } else if (currentSidoSigunguStats?.Items) {
        // 기존 방식 fallback
        const regionName = `제주특별자치도 ${marker.name}`;
        const regionStat = currentSidoSigunguStats.Items.find(item => item.RegionName === regionName);
        
        if (regionStat) {
          stats = {
            chargerCount: regionStat.ChargerCount || 0,
            drResourceCount: regionStat.DrResourceCount || 0,
            powerDemand: regionStat.TotalChargerCapacity || 0
          };
        }
      }
    } else if (marker.type === 'sido' && marker.data.regionCode) {
      // 시도 레벨: dashboardData의 제주시 + 서귀포시 데이터 합산
      const jejuData = currentDashboardData.jejuSiData;
      const seoguipoData = currentDashboardData.seoguipoSiData;
      
      if ((jejuData?.Items || seoguipoData?.Items)) {
        const jejuStats = jejuData?.Items?.reduce((acc, item) => ({
          chargerCount: acc.chargerCount + (item.ChargerCount || 0),
          drResourceCount: acc.drResourceCount + (item.DrResourceCount || 0),
          powerDemand: acc.powerDemand + (item.TotalChargerCapacity || 0)
        }), { chargerCount: 0, drResourceCount: 0, powerDemand: 0 }) || { chargerCount: 0, drResourceCount: 0, powerDemand: 0 };
        
        const seoguipoStats = seoguipoData?.Items?.reduce((acc, item) => ({
          chargerCount: acc.chargerCount + (item.ChargerCount || 0),
          drResourceCount: acc.drResourceCount + (item.DrResourceCount || 0),
          powerDemand: acc.powerDemand + (item.TotalChargerCapacity || 0)
        }), { chargerCount: 0, drResourceCount: 0, powerDemand: 0 }) || { chargerCount: 0, drResourceCount: 0, powerDemand: 0 };
        
        stats = {
          chargerCount: jejuStats.chargerCount + seoguipoStats.chargerCount,
          drResourceCount: jejuStats.drResourceCount + seoguipoStats.drResourceCount,
          powerDemand: jejuStats.powerDemand + seoguipoStats.powerDemand
        };
      } else if (currentSidoSigunguStats?.Items) {
        // 기존 방식 fallback
        const jejuCity = currentSidoSigunguStats.Items.find(item => item.RegionName === '제주특별자치도 제주시');
        const seogwipoCity = currentSidoSigunguStats.Items.find(item => item.RegionName === '제주특별자치도 서귀포시');
        
        if (jejuCity || seogwipoCity) {
          const jejuStats = jejuCity ? {
            chargerCount: jejuCity.ChargerCount || jejuCity.count || 0,
            drResourceCount: jejuCity.DrResourceCount || 0,
            powerDemand: jejuCity.TotalChargerCapacity || 0
          } : { chargerCount: 0, drResourceCount: 0, powerDemand: 0 };
          
          const seogwipoStats = seogwipoCity ? {
            chargerCount: seogwipoCity.ChargerCount || seogwipoCity.count || 0,
            drResourceCount: seogwipoCity.DrResourceCount || 0,
            powerDemand: seogwipoCity.TotalChargerCapacity || 0
          } : { chargerCount: 0, drResourceCount: 0, powerDemand: 0 };
          
          stats = {
            chargerCount: jejuStats.chargerCount + seogwipoStats.chargerCount,
            drResourceCount: jejuStats.drResourceCount + seogwipoStats.drResourceCount,
            powerDemand: jejuStats.powerDemand + seogwipoStats.powerDemand
          };
        }
      }
    } else if (marker.type === 'charger' && marker.data.DrResourceSysId) {
      // DR 자원 레벨: dashboardData의 drResourceData 활용
      if (currentDashboardData.drResourceData?.Items) {
        const drResource = currentDashboardData.drResourceData.Items.find(
          item => item.DrResourceSysId === marker.data.DrResourceSysId
        );
        
        if (drResource) {
          stats = {
            chargerCount: drResource.Chargers?.length || 0,
            drResourceCount: 1,
            powerDemand: drResource.Stations?.reduce((total, station) => 
              total + (station.TotalPowerDemand || 0), 0) || 0
          };
        }
      }
    }

    return stats;
  }, []); // 의존성 배열을 비워서 완전히 안정화 (ref를 통해 최신 값 접근)

  // 마커 클릭 핸들러 (getMarkerStats · scrollStationIntoSidebarRef 이후)
  const handleMarkerClick = (marker) => {
    setSelectedMarkerId(marker.id);

    if (marker.type === 'charger') {
      if (map) {
        map.panTo(new navermaps.LatLng(marker.lat, marker.lng), {
          duration: 500,
          easing: 'easeInOutCubic',
        });
        setMapCenter({ lat: marker.lat, lng: marker.lng });
      }
      const stationSysId = marker.data?.StationSysId;
      if (stationSysId) {
        setSelectedSidebarStationSysId(stationSysId);
        scrollStationIntoSidebarRef.current?.(stationSysId);
      }
    } else {
      const newRegionInfo = {
        regionCode: marker.data?.regionCode ? marker.data.regionCode.toString().slice(0, 8) : null,
        sido: marker.data?.sido || '제주특별자치도',
        sigungu: marker.data?.sigungu || null,
        eupmyeondong: marker.data?.eupmyeondong || null,
      };

      setClickedRegionInfo(newRegionInfo);
      setSelectedSidebarStationSysId(null);

      const stats = getMarkerStats(marker);
      setMiniDashboardData({
        ...marker,
        stats,
      });
      setShowMiniDashboard(true);
    }
  };

  // 지역 선택 핸들러 (기존 방식)
  const handleRegionSelectOld = (level, value) => {
    const currentRegion = selectedRegion;
    const newRegion = { ...currentRegion };
    
    // 지역이 변경되면 DR 자원 뷰로 리셋
    if (level === 'sido' && currentRegion.sido !== value) {
      setSidebarMode('dr-resources');
      setBreadcrumbPath(['DR 자원']);
      setSelectedDRResource(null);
    } else if (level === 'sigungu' && currentRegion.sigungu !== value) {
      setSidebarMode('dr-resources');
      setBreadcrumbPath(['DR 자원']);
      setSelectedDRResource(null);
    } else if (level === 'eupmyeondong' && currentRegion.eupmyeondong !== value) {
      setSidebarMode('dr-resources');
      setBreadcrumbPath(['DR 자원']);
      setSelectedDRResource(null);
    }
    
    if (level === 'sido') {
      newRegion.sido = value;
      newRegion.sigungu = '';
      newRegion.eupmyeondong = '';
      // 시도 레벨로 줌인 (시군구 레벨)
      if (map) {
        map.setZoom(12);
        // 제주도 중심으로 이동
        map.panTo(new navermaps.LatLng(REGION_CENTERS['제주도'].lat, REGION_CENTERS['제주도'].lng), {
            duration: 500,
            easing: 'easeInOutCubic'
          });
        setMapCenter(REGION_CENTERS['제주도']);
      }
    } else if (level === 'sigungu') {
      newRegion.sigungu = value;
      newRegion.eupmyeondong = '';
      // 시군구 레벨로 줌인 (읍면동 레벨)
      if (map) {
        map.setZoom(15);
        // 선택된 시군구 중심으로 이동
        const center = REGION_CENTERS[value];
        if (center) {
          map.panTo(new navermaps.LatLng(center.lat, center.lng), {
            duration: 500,
            easing: 'easeInOutCubic'
          });
          setMapCenter(center);
        }
      }
    } else if (level === 'eupmyeondong') {
      newRegion.eupmyeondong = value;
      // 읍면동 레벨로 줌인 (충전소 레벨)
      if (map) {
        map.setZoom(16);
        // 선택된 읍면동 좌표로 이동
        const eupmyeondongData = jejuRegionData.find(item => 
          item.RegionSido === newRegion.sido && 
          item.RegionSigungu === newRegion.sigungu && 
          item.RegionEupMyuonDong === value
        );
        if (eupmyeondongData) {
          const center = { lat: eupmyeondongData.Latitude, lng: eupmyeondongData.Longitude };
          map.panTo(new navermaps.LatLng(center.lat, center.lng), {
            duration: 500,
            easing: 'easeInOutCubic'
          });
          setMapCenter(center);
        }
      }
    }
    
    // URL 업데이트 (DR 자원 ID는 제거)
    updateURL(newRegion);
  };
  
  // DR 자원 클릭 핸들러
  const handleDRResourceClick = (resource) => {
    setSelectedDRResource(resource);
    setSidebarMode('stations');
    setBreadcrumbPath(['DR 자원', resource.ResourceName]);
    
    // 클릭된 지역 정보 초기화 (DR 자원 클릭 시)
    setClickedRegionInfo(null);
    
    // DR 자원의 지역 정보를 유지하면서 URL 업데이트
    const regionParams = {
      sido: resource.RegionSido,
      sigungu: resource.RegionSigungu, // 올바른 필드명 사용
      eupmyeondong: resource.RegionEupMyuonDong // 올바른 필드명 사용
    };
    updateURL(regionParams, resource.DrResourceSysId);
    
    // 미니 대시보드 데이터 업데이트 (DR 자원 클릭 시)
    updateMiniDashboardForDRResource(resource);
    
    // 첫번째 충전소로 지도 이동
    // resource에 Stations가 있으면 사용, 없으면 dashboardData에서 가져오기
    setTimeout(() => {
      if (!map) return;
      
      let firstStation = null;
      
      // resource에 Stations가 있으면 사용
      if (resource.Stations && resource.Stations.length > 0) {
        firstStation = resource.Stations[0];
      } 
      // 없으면 dashboardData에서 가져오기
      else if (dashboardData?.drResourceData?.Items?.[0]?.Stations?.length > 0) {
        firstStation = dashboardData.drResourceData.Items[0].Stations[0];
      }
      
      if (firstStation) {
        const lat = firstStation.Lat || firstStation.lat;
        const lng = firstStation.Lng || firstStation.lng;
        
        if (lat && lng) {
          map.panTo(new navermaps.LatLng(lat, lng), {
            duration: 500,
            easing: 'easeInOutCubic'
          });
          setMapCenter({ lat: lat, lng: lng });
          // 충전소 레벨로 줌
          map.setZoom(16);
        }
      }
    }, 200); // delay 추가
  };
  
  // 충전소 클릭 핸들러
  const handleStationClick = useCallback((station) => {
    if (station?.StationSysId) {
      setSelectedSidebarStationSysId(station.StationSysId);
    }
    const lat = station.Lat || station.lat;
    const lng = station.Lng || station.lng;

    if (map && lat && lng) {
      map.panTo(new navermaps.LatLng(lat, lng), {
        duration: 500,
        easing: 'easeInOutCubic',
      });
      setMapCenter({ lat, lng });
      map.setZoom(16);
    }
  }, [map]);
  
  // 지역 변경 시 미니 대시보드 데이터 업데이트
  const updateMiniDashboardForRegion = useCallback((region) => {
    
    // 에러가 발생한 경우 즉시 반환 (추가 안전장치)
    if (errorOccurredRef.current || schedulerStoppedRef.current) {
      console.warn('[MapDashboard] updateMiniDashboardForRegion: 에러 발생으로 인해 업데이트 건너뜀');
      return;
    }
    
    // 현재 miniDashboardData와 비교하여 실제로 변경되었을 때만 업데이트 (무한 렌더링 방지)
    const getNewMiniDashboardData = () => {
      if (region.eupmyeondong) {
        // 읍면동 레벨
        const eupmyeondongData = jejuRegionData.find(item => 
          item.RegionSido === region.sido && 
          item.RegionSigungu === region.sigungu && 
          item.RegionEupMyuonDong === region.eupmyeondong
        );
        
        if (eupmyeondongData) {
          const stats = getMarkerStats({
            type: 'eupmyeondong',
            name: region.eupmyeondong,
            data: { 
              regionCode: eupmyeondongData.RegionCode,
              sido: region.sido,
              sigungu: region.sigungu,
              eupmyeondong: region.eupmyeondong
            }
          });
          
          return {
            type: 'eupmyeondong',
            name: region.eupmyeondong,
            lat: eupmyeondongData.Latitude,
            lng: eupmyeondongData.Longitude,
            data: { regionCode: eupmyeondongData.RegionCode },
            stats
          };
        }
      } else if (region.sigungu) {
        // 시군구 레벨 - REGION_CENTERS에서 좌표와 지역코드 가져오기
        const sigunguCenter = REGION_CENTERS[region.sigungu];
        
        if (sigunguCenter) {
          const stats = getMarkerStats({
            type: 'sigungu',
            name: region.sigungu,
            data: { regionCode: sigunguCenter.regionCode }
          });
          
          return {
            type: 'sigungu',
            name: region.sigungu,
            lat: sigunguCenter.lat,
            lng: sigunguCenter.lng,
            data: { regionCode: sigunguCenter.regionCode },
            stats
          };
        }
      } else if (region.sido) {
        // 시도 레벨
        const sidoCenter = REGION_CENTERS['제주도'];
        const stats = getMarkerStats({
          type: 'sido',
          name: '제주특별자치도',
          data: { regionCode: sidoCenter.regionCode }
        });
        
        return {
          type: 'sido',
          name: '제주특별자치도',
          lat: sidoCenter.lat,
          lng: sidoCenter.lng,
          data: { regionCode: sidoCenter.regionCode },
          stats
        };
      }
      return null;
    };
    
    const newData = getNewMiniDashboardData();
    if (!newData) {
      // 데이터가 없으면 미니 대시보드를 닫고 종료
      setShowMiniDashboard(false);
      setMiniDashboardData(null);
      return;
    }
    
    // 현재 데이터와 비교하여 실제로 변경되었을 때만 상태 업데이트
    setMiniDashboardData(prevData => {
      if (prevData && 
          prevData.type === newData.type &&
          prevData.name === newData.name &&
          prevData.data?.regionCode === newData.data?.regionCode &&
          JSON.stringify(prevData.stats) === JSON.stringify(newData.stats)) {
        // 데이터가 동일하면 상태를 업데이트하지 않음 (무한 렌더링 방지)
        return prevData;
      }
      return newData;
    });
    
    // 미니 대시보드 표시 (이미 표시 중이면 업데이트하지 않음)
    setShowMiniDashboard(prev => prev || true);
  }, []); // getMarkerStats는 완전히 안정화되었고, jejuRegionData는 상수이므로 의존성 배열 제거
  
  // DR 자원 클릭 시 미니 대시보드 데이터 업데이트
  const updateMiniDashboardForDRResource = useCallback((resource) => {
    
    // DR 자원의 실제 데이터를 사용하여 통계 계산
    const chargerCount = resource.Chargers?.length || 0;
    const totalPowerDemand = resource.Stations?.reduce((total, station) => {
      return total + (station.TotalPowerDemand || 0);
    }, 0) || 0;
    
    const stats = {
      chargerCount: chargerCount,
      drResourceCount: 1, // DR 자원 자체
      powerDemand: totalPowerDemand
    };
    
    
    // DR 자원의 좌표는 첫번째 충전소의 좌표를 사용
    const firstStation = resource.Stations?.[0];
    const lat = firstStation?.Lat || firstStation?.lat;
    const lng = firstStation?.Lng || firstStation?.lng;
    
    const newMiniDashboardData = {
      type: 'charger',
      name: resource.ResourceName,
      lat: lat,
      lng: lng,
      data: { 
        DrResourceSysId: resource.DrResourceSysId,
        stationId: firstStation?.StationSysId // 첫번째 충전소 ID
      },
      stats
    };
    
    // 현재 데이터와 비교하여 실제로 변경되었을 때만 상태 업데이트 (무한 렌더링 방지)
    setMiniDashboardData(prevData => {
      if (prevData && 
          prevData.type === newMiniDashboardData.type &&
          prevData.data?.DrResourceSysId === newMiniDashboardData.data?.DrResourceSysId &&
          JSON.stringify(prevData.stats) === JSON.stringify(newMiniDashboardData.stats)) {
        // 데이터가 동일하면 상태를 업데이트하지 않음 (무한 렌더링 방지)
        return prevData;
      }
      return newMiniDashboardData;
    });
    
    // 미니 대시보드 표시 (이미 표시 중이면 업데이트하지 않음)
    setShowMiniDashboard(prev => prev || true);
  }, []);
  
  // 지역 선택 핸들러 (RegionSelector용)
  const handleRegionSelect = (newRegion) => {
    const currentRegion = selectedRegion;
    
    // 지역이 변경되면 DR 자원 뷰로 리셋
    if (currentRegion.sido !== newRegion.sido || 
        currentRegion.sigungu !== newRegion.sigungu || 
        currentRegion.eupmyeondong !== newRegion.eupmyeondong) {
      setSidebarMode('dr-resources');
      setBreadcrumbPath(['DR 자원']);
      setSelectedDRResource(null);
      
      // 클릭된 지역 정보 초기화 (지역 선택 변경 시)
      setClickedRegionInfo(null);
      
      // 미니 대시보드 데이터 업데이트 (지역 변경 시)
      updateMiniDashboardForRegion(newRegion);
    }
    
    // URL 업데이트
    updateURL(newRegion);
    
    // 지도 중심과 줌레벨 조정 (동기 문제 해결을 위한 delay 추가)
    setTimeout(() => {
      if (newRegion.eupmyeondong) {
        // 읍면동 레벨로 줌
        if (map) {
          map.setZoom(16);
          // 선택된 읍면동 좌표로 이동
          const eupmyeondongData = jejuRegionData.find(item => 
            item.RegionSido === newRegion.sido && 
            item.RegionSigungu === newRegion.sigungu && 
            item.RegionEupMyuonDong === newRegion.eupmyeondong
          );
          if (eupmyeondongData) {
            const center = { lat: eupmyeondongData.Latitude, lng: eupmyeondongData.Longitude };
            map.panTo(new navermaps.LatLng(center.lat, center.lng), {
              duration: 500,
              easing: 'easeInOutCubic'
            });
            setMapCenter(center);
          }
        }
      } else if (newRegion.sigungu) {
        // 시군구 레벨로 줌
        if (map) {
          map.setZoom(15);
          // 선택된 시군구 중심으로 이동
          const center = REGION_CENTERS[newRegion.sigungu];
          if (center) {
            map.panTo(new navermaps.LatLng(center.lat, center.lng), {
              duration: 500,
              easing: 'easeInOutCubic'
            });
            setMapCenter(center);
          }
        }
      } else if (newRegion.sido) {
        // 시도 레벨로 줌
        if (map) {
          map.setZoom(12);
          // 제주도 중심으로 이동
          map.panTo(new navermaps.LatLng(REGION_CENTERS['제주도'].lat, REGION_CENTERS['제주도'].lng), {
            duration: 500,
            easing: 'easeInOutCubic'
          });
          setMapCenter(REGION_CENTERS['제주도']);
        }
      }
    }, 100); // 100ms delay 추가
  };
  
  // 사이드바 뒤로가기 핸들러
  const handleSidebarBack = () => {
    // if (sidebarMode === 'stations') {
    //   setSidebarMode('dr-resources');
    //   setBreadcrumbPath(['DR 자원']);
    //   setSelectedDRResource(null);
    //   // URL에서 DR 자원 ID 제거
    //   updateURL(selectedRegion);
    // }
    // else{
    // }
    navigate(-1);
  };
  
  // 미니 대시보드 닫기
  const handleCloseMiniDashboard = () => {
    setShowMiniDashboard(false);
    // miniDashboardData는 유지 (핸들바가 계속 보이도록)
    // 클릭된 지역 정보 초기화
    setClickedRegionInfo(null);
  };

  // 핸들바 클릭 핸들러
  const handleHandleClick = () => {
    setShowMiniDashboard(!showMiniDashboard);
  };
  

  // URL 변경에 따른 사이드바 상태 동기화
  // 이전 값 추적을 통해 실제로 변경되었을 때만 상태 업데이트 (무한 렌더링 방지)
  const prevDrResourceIdRef = useRef(urlParams.drResourceId);
  const prevSelectedDRResourceIdRef = useRef(selectedDRResource?.DrResourceSysId);
  
  useEffect(() => {
    const currentDrResourceId = urlParams.drResourceId;
    const currentSelectedDRResourceId = selectedDRResource?.DrResourceSysId;
    
    // 값이 변경되지 않았으면 업데이트하지 않음
    if (prevDrResourceIdRef.current === currentDrResourceId && 
        prevSelectedDRResourceIdRef.current === currentSelectedDRResourceId) {
      return;
    }
    
    // 이전 값 업데이트
    prevDrResourceIdRef.current = currentDrResourceId;
    prevSelectedDRResourceIdRef.current = currentSelectedDRResourceId;
    
    if (currentDrResourceId) {
      // dashboardData에서 DR 자원 데이터 가져오기 (Stations 포함, 더 빠르게 로드됨)
      const drResourceData = dashboardDataRef.current?.drResourceData?.Items?.[0];
      
      if (drResourceData) {
        // DR 자원이 선택된 상태로 업데이트 (변경되었을 때만)
        setSelectedDRResource(prev => 
          prev?.DrResourceSysId === drResourceData.DrResourceSysId ? prev : drResourceData
        );
        setSidebarMode('stations');
        setBreadcrumbPath(['DR 자원', drResourceData.ResourceName]);
      } else if (sidebarDrResources) {
        // dashboardData가 아직 로드되지 않았으면 sidebarDrResources에서 찾기 (fallback)
        const selectedResource = sidebarDrResources.find(
          resource => resource.DrResourceSysId === currentDrResourceId
        );
        
        if (selectedResource) {
          setSelectedDRResource(prev => 
            prev?.DrResourceSysId === selectedResource.DrResourceSysId ? prev : selectedResource
          );
          setSidebarMode('stations');
          setBreadcrumbPath(['DR 자원', selectedResource.ResourceName]);
        }
      }
    } else {
      // DR 자원이 선택되지 않은 상태로 업데이트 (변경되었을 때만)
      setSidebarMode('dr-resources');
      setBreadcrumbPath(['DR 자원']);
      setSelectedDRResource(prev => prev ? null : prev);
    }
  }, [urlParams.drResourceId, sidebarDrResources, selectedDRResource?.DrResourceSysId]);

  // 함수 참조를 ref로 저장 (의존성 배열에서 제거하여 무한 렌더링 방지)
  const updateMiniDashboardForRegionRef = useRef(updateMiniDashboardForRegion);
  const updateMiniDashboardForDRResourceRef = useRef(updateMiniDashboardForDRResource);
  updateMiniDashboardForRegionRef.current = updateMiniDashboardForRegion;
  updateMiniDashboardForDRResourceRef.current = updateMiniDashboardForDRResource;
  
  // dashboardData의 상태를 ref로 추적 (의존성 배열에서 제거하여 무한 렌더링 방지)
  const isLoadingRef = useRef(dashboardData.isLoading);
  const hasErrorRefForEffect = useRef(hasError);
  
  useEffect(() => {
    isLoadingRef.current = dashboardData.isLoading;
    hasErrorRefForEffect.current = hasError;
  }, [dashboardData.isLoading, hasError]);

  // URL로 직접 접근할 때 초기 미니 대시보드 업데이트
  // URL 파라미터가 변경될 때마다 초기화 플래그를 리셋
  const hasInitializedRef = useRef({});
  const prevMiniDashboardDataKeyRef = useRef(null);
  const prevUrlKeyRef = useRef(null);
  const retryAttemptedRef = useRef({}); // URL별로 재시도 여부 추적 (단 1회만)
  const miniDashboardDataRef = useRef(miniDashboardData); // miniDashboardData를 ref로 추적 (무한 렌더링 방지)
  
  // miniDashboardData를 ref로 동기화
  useEffect(() => {
    miniDashboardDataRef.current = miniDashboardData;
  }, [miniDashboardData]);
  
  // dashboardData.drResourceData의 DrResourceSysId를 추출하여 의존성으로 사용 (무한 렌더링 방지)
  const drResourceSysId = dashboardData?.drResourceData?.Items?.[0]?.DrResourceSysId;
  const drResourceStationsLength = dashboardData?.drResourceData?.Items?.[0]?.Stations?.length;
  
  useEffect(() => {
    // API 오류/로딩 중이면 건너뛰기
    if (hasErrorRefForEffect.current || 
        isLoadingRef.current || 
        errorOccurredRef.current || 
        schedulerStoppedRef.current) {
      return;
    }
    
    const { sido, sigungu, eupmyeondong, drResourceId } = urlParams;
    
    // URL 파라미터를 키로 사용하여 초기화 여부 확인
    const urlKey = `${sido}-${sigungu}-${eupmyeondong}-${drResourceId}`;
    
    // URL이 변경되면 이전 URL의 초기화 플래그는 유지하되, 현재 URL에 대해서는 새로 체크
    if (prevUrlKeyRef.current !== urlKey) {
      prevUrlKeyRef.current = urlKey;
      // URL이 변경되면 prevMiniDashboardDataKeyRef도 리셋하여 새로운 데이터를 받을 수 있도록 함
      prevMiniDashboardDataKeyRef.current = null;
      retryAttemptedRef.current[urlKey] = false; // URL 변경 시 재시도 플래그도 리셋
    }
    
    // 데이터가 없으면 미니 대시보드를 닫고 종료
    if (!sido && !sigungu && !eupmyeondong && !drResourceId) {
      setShowMiniDashboard(false);
      setMiniDashboardData(null);
      hasInitializedRef.current[urlKey] = true;
      return;
    }
    
    // 초기 로드 시 미니 대시보드 업데이트
    // drResourceId가 있으면 DR 자원 기반으로만 업데이트 (지역 정보 무시)
    if (drResourceId) {
      // dashboardData에서 DR 자원 데이터 가져오기 (Stations 포함, 더 빠르게 로드됨)
      const drResourceData = dashboardDataRef.current?.drResourceData?.Items?.[0];
      const currentDataKey = drResourceData?.DrResourceSysId 
        ? `${drResourceData.DrResourceSysId}-${drResourceData.Stations?.length || 0}`
        : null;
      
      // 이전 값과 비교하여 실제로 변경되었을 때만 실행
      // URL이 변경되었거나 데이터가 새로 로드되었을 때 업데이트
      if (drResourceData && prevMiniDashboardDataKeyRef.current !== currentDataKey) {
        prevMiniDashboardDataKeyRef.current = currentDataKey;
        updateMiniDashboardForDRResourceRef.current(drResourceData);
        hasInitializedRef.current[urlKey] = true;
        retryAttemptedRef.current[urlKey] = false; // 성공적으로 업데이트되면 재시도 플래그 리셋
      } else if (drResourceData && !miniDashboardDataRef.current && !retryAttemptedRef.current[urlKey]) {
        // miniDashboardData가 null이고 아직 재시도하지 않았으면 1회만 재시도
        retryAttemptedRef.current[urlKey] = true;
        updateMiniDashboardForDRResourceRef.current(drResourceData);
        hasInitializedRef.current[urlKey] = true;
      }
      // dashboardData가 아직 로드되지 않았으면 대기 (다음 렌더링에서 다시 시도)
    } else if (sido || sigungu || eupmyeondong) {
      // drResourceId가 없을 때만 지역 정보로 업데이트
      if (!hasInitializedRef.current[urlKey]) {
        updateMiniDashboardForRegionRef.current({ sido, sigungu, eupmyeondong });
        hasInitializedRef.current[urlKey] = true;
      }
    }
  }, [urlParams.sido, urlParams.sigungu, urlParams.eupmyeondong, urlParams.drResourceId, drResourceSysId, drResourceStationsLength]);
  
  // miniDashboardData가 null이고 drResourceData가 로드되었을 때 재시도 (단 1회만)
  // drResourceSysId가 변경될 때마다 체크하여 데이터가 로드되었는지 확인
  const prevDrResourceSysIdRef = useRef(null);
  useEffect(() => {
    if (!urlParams.drResourceId) return;
    
    const { sido, sigungu, eupmyeondong, drResourceId } = urlParams;
    const urlKey = `${sido}-${sigungu}-${eupmyeondong}-${drResourceId}`;
    
    // drResourceSysId가 변경되었는지 확인
    const currentDrResourceSysId = drResourceSysId;
    if (prevDrResourceSysIdRef.current === currentDrResourceSysId) {
      return; // 변경되지 않았으면 실행하지 않음
    }
    
    // drResourceData가 로드되었고, miniDashboardData가 null이고, 아직 재시도하지 않았으면
    const drResourceData = dashboardDataRef.current?.drResourceData?.Items?.[0];
    if (drResourceData && 
        currentDrResourceSysId && 
        !miniDashboardDataRef.current && 
        !retryAttemptedRef.current[urlKey]) {
      prevDrResourceSysIdRef.current = currentDrResourceSysId;
      retryAttemptedRef.current[urlKey] = true;
      updateMiniDashboardForDRResourceRef.current(drResourceData);
      hasInitializedRef.current[urlKey] = true;
    } else if (currentDrResourceSysId) {
      // drResourceSysId가 있으면 이전 값 업데이트 (다음 변경을 위해)
      prevDrResourceSysIdRef.current = currentDrResourceSysId;
    }
  }, [urlParams.drResourceId, drResourceSysId, drResourceStationsLength]);

  // URL 파라미터에 따른 지도 상태 설정 (동기 문제 해결을 위한 delay 추가)
  // ref를 사용하여 이전 값을 추적하고 실제로 변경되었을 때만 실행
  const prevMapStateRef = useRef(null);
  
  useEffect(() => {
    if (!map || !mapLoaded) return;

    const { sido, sigungu, eupmyeondong, drResourceId } = urlParams;
    
    // 이전 값과 비교하여 실제로 변경되었을 때만 실행
    const currentStateKey = `${drResourceId || ''}-${sido}-${sigungu}-${eupmyeondong}`;
    if (prevMapStateRef.current === currentStateKey) {
      return; // 변경되지 않았으면 실행하지 않음
    }
    
    // 이전 값 업데이트
    prevMapStateRef.current = currentStateKey;
    
    // 지도 이동을 위한 delay 추가
    setTimeout(() => {
      if (drResourceId) {
        // URL에 읍면동이 있으면 리프레시 직후에도 바로 해당 구역 줌/중심 적용 (DR 데이터 대기 전 제주 전역으로 보이는 문제 방지)
        if (eupmyeondong) {
          const eupmyeondongData = jejuRegionData.find(
            (item) =>
              item.RegionSido === sido &&
              item.RegionSigungu === sigungu &&
              item.RegionEupMyuonDong === eupmyeondong
          );
          if (eupmyeondongData) {
            const center = { lat: eupmyeondongData.Latitude, lng: eupmyeondongData.Longitude };
            map.setZoom(16);
            setZoomLevel(16);
            map.panTo(new navermaps.LatLng(center.lat, center.lng), {
              duration: 500,
              easing: 'easeInOutCubic',
            });
            setMapCenter(center);
          }
        } else {
          // 읍면동 없을 때만 첫 충전소 기준으로 이동
          const drResourceData = dashboardDataRef.current?.drResourceData?.Items?.[0];
          if (drResourceData && drResourceData.Stations && drResourceData.Stations.length > 0) {
            const firstStation = drResourceData.Stations[0];
            const lat = firstStation.Lat || firstStation.lat;
            const lng = firstStation.Lng || firstStation.lng;

            if (lat && lng) {
              map.setZoom(16);
              setZoomLevel(16);
              map.panTo(new navermaps.LatLng(lat, lng), {
                duration: 500,
                easing: 'easeInOutCubic',
              });
              setMapCenter({ lat, lng });
            }
          }
        }
        return;
      }
      
      // drResourceId가 없을 때만 지역 정보로 이동
      if (eupmyeondong) {
        // 읍면동이 선택된 경우 - 읍면동 레벨
        const eupmyeondongData = jejuRegionData.find(item => 
          item.RegionSido === sido && 
          item.RegionSigungu === sigungu && 
          item.RegionEupMyuonDong === eupmyeondong
        );
        if (eupmyeondongData) {
          map.setZoom(16);
          const center = { lat: eupmyeondongData.Latitude, lng: eupmyeondongData.Longitude };
          map.panTo(new navermaps.LatLng(center.lat, center.lng), {
            duration: 500,
            easing: 'easeInOutCubic'
          });
          setMapCenter(center);
        }
      } else if (sigungu) {
        // 시군구가 선택된 경우 - 시군구 레벨
        const center = REGION_CENTERS[sigungu];
        if (center) {
          map.setZoom(15);
          map.panTo(new navermaps.LatLng(center.lat, center.lng), {
            duration: 500,
            easing: 'easeInOutCubic'
          });
          setMapCenter(center);
        }
      } else if (sido) {
        // 시도가 선택된 경우 - 시도 레벨
        const center = REGION_CENTERS['제주도'];
        map.setZoom(12);
        map.panTo(new navermaps.LatLng(center.lat, center.lng), {
          duration: 500,
          easing: 'easeInOutCubic'
        });
        setMapCenter(center);
      }
    }, 100); // 100ms delay 추가
    
    // 지도 이동 완료 후 보이는 마커들 업데이트 (디바운스 적용)
    setTimeout(() => {
      updateVisibleMarkers();
    }, 600);
  }, [map, mapLoaded, urlParams.drResourceId, urlParams.sido, urlParams.sigungu, urlParams.eupmyeondong]);

  // URL로 직접 접근 시 DR 자원이 설정된 후 지도 이동 (동기 문제 해결)
  // dashboardData에서 가져온 DR 자원 데이터 사용 (Stations 포함)
  // ref를 사용하여 이전 값을 추적하고 실제로 변경되었을 때만 실행
  const prevDrResourceDataRef = useRef(null);
  useEffect(() => {
    if (!map || !mapLoaded || !urlParams.drResourceId) return;
    
    // dashboardData에서 DR 자원 데이터 가져오기 (Stations 포함)
    const drResourceData = dashboardDataRef.current?.drResourceData?.Items?.[0];
    
    // 이전 값과 비교하여 실제로 변경되었을 때만 실행
    const currentDataKey = drResourceData?.DrResourceSysId 
      ? `${drResourceData.DrResourceSysId}-${drResourceData.Stations?.[0]?.StationSysId || ''}`
      : null;
    
    if (prevDrResourceDataRef.current === currentDataKey) {
      return; // 변경되지 않았으면 실행하지 않음
    }
    
    if (drResourceData && drResourceData.Stations && drResourceData.Stations.length > 0) {
      const firstStation = drResourceData.Stations[0];
      const lat = firstStation.Lat || firstStation.lat;
      const lng = firstStation.Lng || firstStation.lng;

      // URL에 읍면동이 있으면 이미 행정구역 중심으로 맞춤 — 첫 충전소로 덮어쓰지 않음
      const hasEupmyeondongInUrl = !!urlParams.eupmyeondong?.trim();

      if (lat && lng && !hasEupmyeondongInUrl) {
        prevDrResourceDataRef.current = currentDataKey;

        setTimeout(() => {
          map.setZoom(16);
          setZoomLevel(16);
          map.panTo(new navermaps.LatLng(lat, lng), {
            duration: 500,
            easing: 'easeInOutCubic',
          });
          setMapCenter({ lat, lng });
        }, 200);
      } else if (hasEupmyeondongInUrl) {
        prevDrResourceDataRef.current = currentDataKey;
      }
    }
  }, [map, mapLoaded, urlParams.drResourceId, urlParams.eupmyeondong]);
  
  // dashboardData.drResourceData가 로드되면 마커 업데이트 (ref를 사용하여 무한 렌더링 방지)
  const prevDrResourceDataKeyRef = useRef(null);
  useEffect(() => {
    if (!map || !mapLoaded || !urlParams.drResourceId) return;
    
    const drResourceData = dashboardDataRef.current?.drResourceData?.Items?.[0];
    const currentDataKey = drResourceData?.DrResourceSysId 
      ? `${drResourceData.DrResourceSysId}-${drResourceData.Stations?.length || 0}`
      : null;
    
    // 이전 값과 비교하여 실제로 변경되었을 때만 실행
    if (prevDrResourceDataKeyRef.current === currentDataKey) {
      return;
    }
    
    if (drResourceData && drResourceData.Stations && drResourceData.Stations.length > 0) {
      prevDrResourceDataKeyRef.current = currentDataKey;
      // 약간의 지연을 두어 지도 이동과 충돌 방지
      setTimeout(() => {
        updateVisibleMarkers();
      }, 300);
    }
  }, [map, mapLoaded, urlParams.drResourceId, drResourceSysId, drResourceStationsLength]);

  // 지도 이벤트 리스너 설정
  useEffect(() => {
    if (map) {
      const loadListener = navermaps.Event.addListener(map, 'init', handleMapLoad);
      const zoomListener = navermaps.Event.addListener(map, 'zoom_changed', handleMapZoomChanged);
      const dragListener = navermaps.Event.addListener(map, 'dragend', handleMapDragEnd);
      
      
      
      // 지도가 이미 준비된 상태라면 mapLoaded를 true로 설정
      if (map.getZoom && typeof map.getZoom === 'function') {
        try {
          const zoom = map.getZoom();
          if (zoom !== undefined && zoom !== null) {
            setMapLoaded(true);
            // 초기 보이는 마커들 업데이트
            setTimeout(() => {
              updateVisibleMarkers();
            }, 100);
          }
        } catch (error) {
        }
      }
      
      return () => {
        // 타이머 정리
        if (updateTimerRef.current) {
          clearTimeout(updateTimerRef.current);
        }
        navermaps.Event.removeListener(loadListener);
        navermaps.Event.removeListener(zoomListener);
        navermaps.Event.removeListener(dragListener);
      };
    } else {
    }
  }, [map]);


  // 마커 root 캐시를 위한 ref
  const markerRootsRef = useRef(new Map());
  const lastRenderedMarkersRef = useRef(new Set());

  // 충전소 마커 렌더링 (줌 레벨 15 이상에서만 표시)
  useEffect(() => {
    const chargerMarkers = (mapMarkers || []).filter(marker => marker.type === 'charger');
    const currentMarkerIds = new Set(chargerMarkers.map(marker => marker.id));

    // mapMarkers가 비어 있으면(예: DR 모드 저줌) 예전 effect가 스킵되어 root/ref가 남던 버그 방지
    if (zoomLevel < 15 || chargerMarkers.length === 0) {
      markerRootsRef.current.forEach((root) => {
        try {
          root.unmount();
        } catch (error) {
          console.warn('Root unmount error:', error);
        }
      });
      markerRootsRef.current.clear();
      lastRenderedMarkersRef.current = new Set();
      return;
    }

    const hasChanged =
      currentMarkerIds.size !== lastRenderedMarkersRef.current.size ||
      [...currentMarkerIds].some(id => !lastRenderedMarkersRef.current.has(id));

    if (!hasChanged) {
      chargerMarkers.forEach(marker => {
        const markerElement = document.getElementById(`station-marker-${marker.id}`);
        if (markerElement) {
          const root = markerRootsRef.current.get(marker.id);
          if (root) {
            const stationStats = getMarkerStats(marker);
            const fallbackChargerStats = calculateChargerStats(marker.data);

            root.render(
              <QueryClientProvider client={queryClient}>
                <DotMarker
                  station={marker.data}
                  markerName={marker.name}
                  onClick={() => handleMarkerClick(marker)}
                  stats={stationStats}
                  fallbackChargerStats={fallbackChargerStats}
                />
              </QueryClientProvider>
            );
          }
        }
      });
      return;
    }

    markerRootsRef.current.forEach((root, markerId) => {
      if (!currentMarkerIds.has(markerId)) {
        try {
          root.unmount();
        } catch (error) {
          console.warn('Root unmount error:', error);
        }
        markerRootsRef.current.delete(markerId);
      }
    });

    chargerMarkers.forEach(marker => {
      const markerElement = document.getElementById(`station-marker-${marker.id}`);
      if (markerElement) {
        const stationStats = getMarkerStats(marker);
        const fallbackChargerStats = calculateChargerStats(marker.data);

        let root = markerRootsRef.current.get(marker.id);
        if (!root) {
          root = createRoot(markerElement);
          markerRootsRef.current.set(marker.id, root);
        }

        root.render(
          <QueryClientProvider client={queryClient}>
            <DotMarker
              station={marker.data}
              markerName={marker.name}
              onClick={() => handleMarkerClick(marker)}
              stats={stationStats}
              fallbackChargerStats={fallbackChargerStats}
            />
          </QueryClientProvider>
        );
      }
    });

    lastRenderedMarkersRef.current = currentMarkerIds;
  }, [mapMarkers, zoomLevel, queryClient]);

  // 컴포넌트 언마운트 시 모든 root 정리
  useEffect(() => {
    return () => {
      markerRootsRef.current.forEach(root => {
        try {
          root.unmount();
        } catch (error) {
          console.warn('Root unmount error:', error);
        }
      });
      markerRootsRef.current.clear();
    };
  }, []);

  // 컴포넌트 언마운트시 타이머 정리
  useEffect(() => {
    return () => {
      if (updateTimerRef.current) {
        clearTimeout(updateTimerRef.current);
      }
    };
  }, []);

  // 읍면동 레벨인지 확인
  const isEupmyeondongLevel = useMemo(() => {
    return !!(selectedRegion.eupmyeondong && selectedRegion.eupmyeondong.trim() !== '');
  }, [selectedRegion.eupmyeondong]);

  const [activeTab, setActiveTab] = useState('stat');

  // 읍면동 레벨이 아닐 때는 항상 'stat' 탭으로 고정
  useEffect(() => {
    if (!isEupmyeondongLevel && activeTab !== 'stat') {
      setActiveTab('stat');
    }
  }, [isEupmyeondongLevel, activeTab]);

  return (
    <div className="map-dashboard-container page-container">
      <div className="map-dashboard-content">
        {/* 지도 영역 */}
        <div className="map-area">
        <MapDiv className="map-container">
          <NaverMap
            ref={setMap}
            mapDivId="map"
            width="100%"
            height="100%"
            center={{ lat: mapCenter.lat, lng: mapCenter.lng }}
            zoom={zoomLevel}
            disableKineticPan={false}
              minZoom={8}
            maxZoom={21}
            mapTypeId={navermaps.MapTypeId.NORMAL}
            mapDataControl={false}
            scaleControl={false}
            logoControl={false}
            mapTypeControl={false}
            zoomControl={false}
            streetViewControl={false}
            restrictBounds={{
              north: JEJU_BOUNDS.north,
              south: JEJU_BOUNDS.south,
              east: JEJU_BOUNDS.east,
              west: JEJU_BOUNDS.west
            }}
            onClick={() => {
              setSelectedMarkerId(null);
              setSelectedSidebarStationSysId(null);
            }}
          >
              {/* 마커들 렌더링 */}
              {mapMarkers.map((marker) => {
                
                // 마커별 통계 데이터 가져오기
                const markerStats = getMarkerStats(marker);
                
                // 지역별 마커 HTML 생성 (빨간색 말풍선 형태)
                const isSelected = selectedMarkerId === marker.id;
                const customMarkerHTML = `
                  <div class="region-marker" style="
                    position: relative;
                    cursor: pointer;
                    z-index: ${isSelected ? 1000 : 100};
                  ">
                    <div class="marker-bubble" style="
                      background: #2563EB;
                      color: white;
                      padding: 8px 12px;
                      border-radius: 8px;
                      font-size: 14px;
                      font-weight: 600;
                      display: flex;
                      align-items: center;
                      gap: 6px;
                      white-space: nowrap;
                      transition: all 0.2s ease;
                    ">
                      <span class="region-name" style="margin-right: 4px;">${marker.name}</span>
                      <svg width="18" height="16" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12.7505 1.0922L9.78281 8.62878C9.53914 9.2476 9.98778 9.92073 10.6439 9.92073H13.2156L11.6592 14.466C11.5032 14.9215 11.7885 15.4101 12.2569 15.4894C12.5575 15.5403 12.8592 15.4054 13.0253 15.1458L17.8486 7.60793C18.2497 6.98122 17.8069 6.15244 17.0711 6.15244H14.6067L16.7178 1.86338C17.0261 1.237 16.5777 0.5 15.8883 0.5H13.6116C13.2324 0.5 12.8913 0.73453 12.7505 1.0922Z" fill="white"/>
                      <path d="M4.17332 3.32622C3.66113 3.32622 3.24592 3.748 3.24592 4.26829C3.24592 4.78859 3.66113 5.21037 4.17332 5.21037H7.41924C7.93143 5.21037 8.34665 4.78859 8.34665 4.26829C8.34665 3.748 7.93143 3.32622 7.41924 3.32622H4.17332Z" fill="white"/>
                      <path d="M4.17332 10.8628C3.66113 10.8628 3.24592 11.2846 3.24592 11.8049C3.24592 12.3252 3.66113 12.747 4.17332 12.747H7.41924C7.93143 12.747 8.34665 12.3252 8.34665 11.8049C8.34665 11.2846 7.93143 10.8628 7.41924 10.8628H4.17332Z" fill="white"/>
                      <path d="M0 8.03659C0 7.51629 0.415213 7.09451 0.927405 7.09451H4.17332C4.68552 7.09451 5.10073 7.51629 5.10073 8.03659C5.10073 8.55688 4.68552 8.97866 4.17332 8.97866H0.927405C0.415213 8.97866 0 8.55688 0 8.03659Z" fill="white"/>
                      </svg>

                      <span class="dr-count" style="
                        font-size: 16px;
                        font-weight: bold;
                      ">${markerStats.drResourceCount || 0}</span>
                    </div>
                  </div>
                `;

                // 충전소 마커는 카드 형태로 표시
                if (marker.type === 'charger') {
                  const stationStats = getMarkerStats(marker);
                  const stationData = marker.data;
                  
                  // 충전소 상태 데이터 계산 (EVChargerCard와 동일한 방식)
                  const chargerStats = calculateChargerStats(stationData);
                  const waitingCount = chargerStats.waiting;
                  const inUseCount = chargerStats.using;
                  const unavailableCount = chargerStats.unavailable;
                  
                  const isStationSelected = selectedMarkerId === marker.id;
                  // 점 마커용 간단한 컨테이너 (hover 시 StationMarker를 위한 충분한 공간)
                  const stationMarkerHTML = `
                    <div id="station-marker-${marker.id}" style="
                      width: 300px;
                      height: 200px;
                      position: relative;
                      cursor: pointer;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                    "></div>
                  `;
                  
                  // 기존 카드 형태 마커 (주석 처리)
                  const stationMarkerHTMLOld = `
                    <div class="station-marker" style="
                      position: relative;
                      cursor: pointer;
                      z-index: ${isStationSelected ? 1000 : 100};
                    ">
                      <div class="station-card" style="
                        background: white;
                        border-radius: 8px;
                        box-shadow: ${isStationSelected ? '0 8px 24px rgba(0, 0, 0, 0.25)' : '0 4px 12px rgba(0, 0, 0, 0.15)'};
                        border: ${isStationSelected ? '2px solid #2563EB' : 'none'};
                        padding: 12px;
                        min-width: 200px;
                        max-width: 250px;
                        display: grid;
                        grid-template-columns: 32px 1fr;
                        transform: ${isStationSelected ? 'scale(1.05)' : 'scale(1)'};
                        transition: all 0.2s ease;
                      ">
                        <div>
                          <div class="station-address" style="
                          display: flex;
                          align-items: center;
                          justify-content: space-between;
                          gap: 6px;
                          height: 100%;
                        ">
                          <svg width="16" height="18" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8.24914 3.61409C8.23656 3.60938 8.22374 3.60524 8.21072 3.60168C8.16929 3.59034 8.12731 3.58527 8.08592 3.58601H6.99996C6.78942 3.58601 6.60874 3.73362 6.56995 3.93731L5.91449 7.37889C5.91219 7.39043 5.91037 7.40203 5.90904 7.41367C5.90028 7.48969 5.91242 7.56443 5.94162 7.63146C5.95011 7.65096 5.96011 7.66997 5.97164 7.68832C5.97735 7.69743 5.98339 7.70628 5.98972 7.71487C6.03842 7.78098 6.10644 7.83387 6.18838 7.86448C6.20116 7.86926 6.21419 7.87346 6.22742 7.87705C6.26864 7.88828 6.31042 7.89329 6.35159 7.89256H7.62961L7.22631 9.96649C7.18611 10.1776 7.31005 10.3856 7.51684 10.454C7.72364 10.5225 7.95007 10.4305 8.04764 10.2384L9.79757 6.79318C9.86537 6.65968 9.85812 6.50114 9.77841 6.37418C9.69869 6.24722 9.55787 6.16994 9.40624 6.16994H8.12043L8.52304 4.09968C8.52539 4.0879 8.52723 4.07605 8.52857 4.06416C8.53717 3.98839 8.52501 3.91392 8.4959 3.84711C8.48742 3.82761 8.47741 3.8086 8.46589 3.79025C8.4606 3.78182 8.45503 3.77361 8.44921 3.76562C8.40041 3.69861 8.33187 3.645 8.24914 3.61409Z" fill="#2563EB"/>
                            <path fill-rule="evenodd" clip-rule="evenodd" d="M1.96875 3.09375C1.96875 1.46278 3.29091 0.140625 4.92188 0.140625H10.8281C12.4591 0.140625 13.7812 1.46278 13.7812 3.09375V15.8906H14.7656C15.3093 15.8906 15.75 16.3313 15.75 16.875C15.75 17.4187 15.3093 17.8594 14.7656 17.8594H0.984375C0.44072 17.8594 0 17.4187 0 16.875C0 16.3313 0.44072 15.8906 0.984375 15.8906H1.96875V3.09375ZM11.8125 3.09375V15.8906H3.9375V3.09375C3.9375 2.55009 4.37822 2.10938 4.92188 2.10938H10.8281C11.3718 2.10938 11.8125 2.55009 11.8125 3.09375Z" fill="#2563EB"/>
                          </svg>
                        </div>
                        </div>
                        <div style="
                          display: flex;
                          flex-direction: column;
                          gap: 8px;
                        ">
                          <!-- 충전소 이름 -->
                          <div class="station-title" style="
                            font-size: 14px;
                            font-weight: 700;
                            color: #323B49;
                            line-height: 1.2;
                          ">${marker.name}</div>
                          <!-- 충전소 상태 -->
                          <div class="station-status" style="
                            display: grid;
                            grid-template-columns: 1fr 1fr 1fr;
                            gap: 8px;
                          ">
                            <div class="status-item" style="
                              display: flex;
                              align-items: center;
                              gap: 4px;
                            ">
                              <span style="
                                font-size: 12px;
                                color: #6B7280;
                              ">대기</span>
                              <span style="
                                font-size: 12px;
                                font-weight: 600;
                                color: #2563EB;
                              ">${waitingCount}</span>
                            </div>
                            <div class="status-item" style="
                              display: flex;
                              align-items: center;
                              gap: 4px;
                            ">
                              <span style="
                                font-size: 12px;
                                color: #6B7280;
                              ">사용</span>
                              <span style="
                                font-size: 12px;
                                font-weight: 600;
                                color: #10B981;
                              ">${inUseCount}</span>
                            </div>
                            <div class="status-item" style="
                              display: flex;
                              align-items: center;
                              gap: 4px;
                            ">
                              <span style="
                                font-size: 12px;
                                color: #6B7280;
                              ">불가</span>
                              <span style="
                                font-size: 12px;
                                font-weight: 600;
                                color: #F59E0B;
                              ">${unavailableCount}</span>
                            </div>
                          </div>
                        </div>
                        
                      </div>
                    </div>
                  `;  // 기존 카드 형태 마커 HTML 끝 (주석 처리됨)
                  return (
                    <Marker
                      key={marker.id}
                      position={{ lat: marker.lat, lng: marker.lng }}
                      title={marker.name}
                      onClick={() => handleMarkerClick(marker)}
                      icon={{
                        content: stationMarkerHTML,
                        anchor: new navermaps.Point(150, 100) // 컨테이너 중앙에 앵커
                      }}
                    />
                  );
                }
                
                // 지역별 마커들 (시도, 시군구, 읍면동)
                return (
                  <Marker
                    key={marker.id}
                    position={{ lat: marker.lat, lng: marker.lng }}
                    title={marker.name}
                    onClick={() => handleMarkerClick(marker)}
                    icon={{
                      content: customMarkerHTML,
                      anchor: new navermaps.Point(60, 25) // 마커 중앙 하단에 앵커
                  }}
                />
              );
            })}
          </NaverMap>
        </MapDiv>

          {/* 줌 레벨 안내 풍선 (DR 자원 선택 모드에서만 표시) */}
          {urlParams.drResourceId && zoomLevel < 15 && (
            <div className="zoom-notice-balloon">
              <div className="zoom-notice-content">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/>
                  <path d="21 21l-4.35-4.35"/>
                  <path d="M11 8v6"/>
                  <path d="M8 11h6"/>
                </svg>
                <span>충전소를 보려면 지도를 더 확대해주세요</span>
              </div>
            </div>
          )}

          {/* 미니 대시보드 - 지도 내부 */}
          {miniDashboardData && (
            <div className={`mini-dashboard ${showMiniDashboard ? 'open' : ''}`}>
              <div 
                className="mini-dashboard-handle"
                onClick={handleHandleClick}
              ></div>
              <div className="mini-dashboard-header">
                <h2>{miniDashboardData.name}</h2>
                {miniDashboardData.type === 'charger' && <p className="detail-btn" onClick={()=>{navigate(`/dr-management/detail/${miniDashboardData.data?.DrResourceSysId}`, { state: { from: 'map' } })}}>자세히 보기</p>}
              </div>
              <div className='mini-dashboard-tabs-container'>
                <div className="tabs mini-dashboard-tabs" style={{ gridTemplateColumns: isEupmyeondongLevel ? 'repeat(4, 1fr)' : '1fr' }}>
                  <button
                    className={`tab ${activeTab === 'stat' ? 'active' : ''}`}
                    onClick={() => setActiveTab('stat')}
                  >
                    통계
                  </button>
                  {isEupmyeondongLevel && (
                    <>
                      <button
                        className={`tab ${activeTab === 'power' ? 'active' : ''}`}
                        onClick={() => setActiveTab('power')}
                      >
                        실시간 - 어제 전력수요
                      </button>
                      <button
                        className={`tab ${activeTab === 'cbl' ? 'active' : ''}`}
                        onClick={() => setActiveTab('cbl')}
                      >
                        CBL
                      </button>
                      <button
                        className={`tab ${activeTab === 'dr' ? 'active' : ''}`}
                        onClick={() => setActiveTab('dr')}
                      >
                        최근 DR 발령일
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className={`mini-dashboard-content ${activeTab}`}>
                {activeTab === 'stat' && (
                  <>
                    <div className="flex flex-col gap-8">
                      <h3 className="text-b1-700 text-gray-700">2주 통계</h3>
                      <div className="mini-dashboard-summary-info">
                        <div className='flex flex-col gap-8'>
                          <div className="mini-dashboard-summary-item">
                            <div className="mini-dashboard-summary-item-icon charger">
                              <MiniChargerIcon />
                            </div>
                            <div className="mini-dashboard-summary-item-text">
                              <p className="mini-dashboard-summary-item-text-title">총 충전량 (kWh)</p>
                              <p className="mini-dashboard-summary-item-text-value charger">{dashboardData?.regionClusterStatData?.TwoWeeksTotalData?.TotalAoC?.toLocaleString() || 0}</p>
                            </div>
                          </div>
                          <div className="mini-dashboard-summary-item">
                            <div className="mini-dashboard-summary-item-icon bolt">
                              <MiniBoltIcon />
                            </div>
                            <div className="mini-dashboard-summary-item-text">
                              <p className="mini-dashboard-summary-item-text-title">총 충전횟수</p>
                              <p className="mini-dashboard-summary-item-text-value bolt">{dashboardData?.regionClusterStatData?.TwoWeeksTotalData?.TotalChargingCount.toLocaleString() || 0}</p>
                            </div>
                          </div>
                          <div className="mini-dashboard-summary-item">
                            <div className="mini-dashboard-summary-item-icon time">
                              <MiniTimeIcon />
                            </div>
                                <div className="mini-dashboard-summary-item-text">
                                  <p className="mini-dashboard-summary-item-text-title">총 충전시간</p>
                                  <p className="mini-dashboard-summary-item-text-value time">
                                    {formatSecondsToTime(dashboardData?.regionClusterStatData?.TwoWeeksTotalData?.TotalChargingSeconds)}
                                  </p>
                            </div>
                          </div>
                        </div>
                        <div className='flex flex-col gap-8'>
                          <div className="mini-dashboard-summary-item">
                            <div className="mini-dashboard-summary-item-icon charger">
                              <MiniChargerIcon />
                            </div>
                            <div className="mini-dashboard-summary-item-text">
                              <p className="mini-dashboard-summary-item-text-title">일 충전량 평균 (kWh)</p>
                              <p className="mini-dashboard-summary-item-text-value charger">{dashboardData?.regionClusterStatData?.TwoWeeksAvgData?.AvgAoC?.toLocaleString() || 0}</p>
                            </div>
                          </div>
                          <div className="mini-dashboard-summary-item">
                            <div className="mini-dashboard-summary-item-icon bolt">
                              <MiniBoltIcon />
                            </div>
                            <div className="mini-dashboard-summary-item-text">
                              <p className="mini-dashboard-summary-item-text-title">일 충전횟수 평균</p>
                              <p className="mini-dashboard-summary-item-text-value bolt">{dashboardData?.regionClusterStatData?.TwoWeeksAvgData?.AvgChargingCount.toLocaleString() || 0}</p>
                            </div>
                          </div>
                          <div className="mini-dashboard-summary-item">
                            <div className="mini-dashboard-summary-item-icon time">
                              <MiniTimeIcon />
                            </div>
                                <div className="mini-dashboard-summary-item-text">
                                  <p className="mini-dashboard-summary-item-text-title">일 충전시간 평균</p>
                                  <p className="mini-dashboard-summary-item-text-value time">{formatSecondsToTime(dashboardData?.regionClusterStatData?.TwoWeeksAvgData?.AvgChargingSeconds)}</p>
                            </div>
                          </div>
                          <div className="mini-dashboard-summary-item">
                            <div className="mini-dashboard-summary-item-icon charger">
                              <MiniChargerIcon />
                            </div>
                                <div className="mini-dashboard-summary-item-text">
                                  <p className="mini-dashboard-summary-item-text-title">1회당 충전량 평균 (kWh)</p>
                                  <p className="mini-dashboard-summary-item-text-value charger">{dashboardData?.regionClusterStatData?.TwoWeeksAvgData?.AvgAoCPerCharging.toLocaleString() || 0}</p>
                            </div>
                          </div>
                          <div className="mini-dashboard-summary-item">
                            <div className="mini-dashboard-summary-item-icon time">
                              <MiniTimeIcon />
                            </div>
                                <div className="mini-dashboard-summary-item-text">
                                  <p className="mini-dashboard-summary-item-text-title">1회당 충전시간 평균</p>
                                  <p className="mini-dashboard-summary-item-text-value time">{formatSecondsToTime(dashboardData?.regionClusterStatData?.TwoWeeksAvgData?.AvgChargingSecondsPerCharging)}</p>
                            </div>
                          </div>
                          
                        </div>
                        

                      </div>
                    </div>
                    <div className="flex flex-col gap-8">
                      <h3 className="text-b1-700 text-gray-700">1년 통계</h3>
                      <div className="mini-dashboard-summary-info">
                        <div className='flex flex-col gap-8'>
                          <div className="mini-dashboard-summary-item">
                            <div className="mini-dashboard-summary-item-icon charger">
                              <MiniChargerIcon />
                            </div>
                            <div className="mini-dashboard-summary-item-text">
                              <p className="mini-dashboard-summary-item-text-title">총 충전량 (kWh)</p>
                              <p className="mini-dashboard-summary-item-text-value charger">{dashboardData?.regionClusterStatData?.OneYearTotalData?.TotalAoC?.toLocaleString() || 0}</p>
                            </div>
                          </div>
                          <div className="mini-dashboard-summary-item">
                            <div className="mini-dashboard-summary-item-icon bolt">
                              <MiniBoltIcon />
                            </div>
                            <div className="mini-dashboard-summary-item-text">
                              <p className="mini-dashboard-summary-item-text-title">총 충전횟수</p>
                              <p className="mini-dashboard-summary-item-text-value bolt">{dashboardData?.regionClusterStatData?.OneYearTotalData?.TotalChargingCount.toLocaleString() || 0}</p>
                            </div>
                          </div>
                          <div className="mini-dashboard-summary-item">
                            <div className="mini-dashboard-summary-item-icon time">
                              <MiniTimeIcon />
                            </div>
                                <div className="mini-dashboard-summary-item-text">
                                  <p className="mini-dashboard-summary-item-text-title">총 충전시간</p>
                                  <p className="mini-dashboard-summary-item-text-value time">
                                    {formatSecondsToTime(dashboardData?.regionClusterStatData?.OneYearTotalData?.TotalChargingSeconds)}
                                  </p>
                            </div>
                          </div>
                        </div>
                        <div className='flex flex-col gap-8'>
                          <div className="mini-dashboard-summary-item">
                            <div className="mini-dashboard-summary-item-icon charger">
                              <MiniChargerIcon />
                            </div>
                            <div className="mini-dashboard-summary-item-text">
                              <p className="mini-dashboard-summary-item-text-title">일 충전량 평균 (kWh)</p>
                              <p className="mini-dashboard-summary-item-text-value charger">{dashboardData?.regionClusterStatData?.OneYearAvgData?.AvgAoC?.toLocaleString() || 0}</p>
                            </div>
                          </div>
                          <div className="mini-dashboard-summary-item">
                            <div className="mini-dashboard-summary-item-icon bolt">
                              <MiniBoltIcon />
                            </div>
                            <div className="mini-dashboard-summary-item-text">
                              <p className="mini-dashboard-summary-item-text-title">일 충전횟수 평균</p>
                              <p className="mini-dashboard-summary-item-text-value bolt">{dashboardData?.regionClusterStatData?.OneYearAvgData?.AvgChargingCount.toLocaleString() || 0}</p>
                            </div>
                          </div>
                          <div className="mini-dashboard-summary-item">
                            <div className="mini-dashboard-summary-item-icon time">
                              <MiniTimeIcon />
                            </div>
                                <div className="mini-dashboard-summary-item-text">
                                  <p className="mini-dashboard-summary-item-text-title">일 충전시간 평균</p>
                                  <p className="mini-dashboard-summary-item-text-value time">{formatSecondsToTime(dashboardData?.regionClusterStatData?.OneYearAvgData?.AvgChargingSeconds)}</p>
                            </div>
                          </div>
                          <div className="mini-dashboard-summary-item">
                            <div className="mini-dashboard-summary-item-icon charger">
                              <MiniChargerIcon />
                            </div>
                                <div className="mini-dashboard-summary-item-text">
                                  <p className="mini-dashboard-summary-item-text-title">1회당 충전량 평균 (kWh)</p>
                                  <p className="mini-dashboard-summary-item-text-value charger">{dashboardData?.regionClusterStatData?.OneYearAvgData?.AvgAoCPerCharging.toLocaleString() || 0}</p>
                            </div>
                          </div>
                          <div className="mini-dashboard-summary-item">
                            <div className="mini-dashboard-summary-item-icon time">
                              <MiniTimeIcon />
                            </div>
                                <div className="mini-dashboard-summary-item-text">
                                  <p className="mini-dashboard-summary-item-text-title">1회당 충전시간 평균</p>
                                  <p className="mini-dashboard-summary-item-text-value time">{formatSecondsToTime(dashboardData?.regionClusterStatData?.OneYearAvgData?.AvgChargingSecondsPerCharging)}</p>
                            </div>
                          </div>
                        </div>
                        

                      </div>
                    </div>
                  </>
                )}
                {activeTab === 'power' && (
                  <>
                    <MiniDashBoardChart dashboardData={combinedDashboardData} type={activeTab}/>
                  </>
                )}
                {activeTab === 'cbl' && (
                    <>
                    <MiniDashBoardChart dashboardData={combinedDashboardData} type={activeTab}/>
                  </>
                )}
                {activeTab === 'dr' && (
                  <>
                    <MiniDashBoardChart dashboardData={combinedDashboardData} type={activeTab}/>
                  </>
                )}
              </div>
            </div>
          )}
          </div>
          
        {/* 사이드바 영역 */}
        <div className="sidebar">
          {/* 지역 선택 섹션 */}
          <div className="region-selection">
            <RegionSelector
              selectedRegion={selectedRegion}
              onRegionSelect={handleRegionSelect}
              jejuRegionData={jejuRegionData}
              sigunguList={sigunguList}
              eupmyeondongList={eupmyeondongList}
            />
            </div>
          {/* 뒤로가기 */}
          <div className="sidebar-back-container flex align-center">
            <div className="sidebar-back flex align-center" onClick={handleSidebarBack}>
              <ChevronLeftIcon /> 뒤로가기
            </div>
          </div> 
          
          {/* DR 자원 목록 섹션 */}
          {sidebarMode === 'dr-resources' && !urlParams.drResourceId && (
            <div className="dr-resource-list">
              <h3 className="text-b1-700 text-gray-200">
                <span className="dr-resource-list-title-region">{selectedRegion.eupmyeondong || selectedRegion.sigungu || selectedRegion.sido}</span>
                에 할당된 DR 자원</h3>
              {sidebarDrResourcesLoading ? (
                <div className="loading">로딩 중...</div>
              ) : (
                <>
                  {sidebarTotalCount > 0 ? 
                  <p className="dr-resource-list-title-count text-c2-400 text-gray-600 mb-5">
                    <b>{sidebarTotalCount}</b>
                    개의 데이터가 있습니다.</p>
                  : 
                  <div className="no-data text-c2-400 text-gray-600 mb-5">선택된 지역에 DR 자원이 없습니다.</div>
                  }
                  <div className="resource-items">
                    {sidebarDrResources?.map((resource) => (
                      <DRResourceCard 
                        key={resource.DrResourceSysId}
                        resource={resource}
                        onResourceClick={handleDRResourceClick}
                      />
                    ))}
                </div>
                {sidebarTotalCount > pageSize && (
                  <div className="pagination-container" style={{ marginTop: '16px', display: 'flex', justifyContent: 'center' }}>
                    <Pagination
                      currentPage={drResourcePage}
                      totalPages={Math.max(1, Math.ceil(sidebarTotalCount / pageSize))}
                      onPageChange={handlePageChange}
                      size="small"
                    />
                  </div>
                )}
              </>
            )}
          </div>
          )}
          
          {/* 충전소 목록 섹션 */}
          {(sidebarMode === 'stations' || urlParams.drResourceId) && (
            <div className="station-list">
              <h3>
                <span className="station-list-title-region">
                  {selectedDRResource?.ResourceName || dashboardDataRef.current?.drResourceData?.Items?.[0]?.ResourceName || 'DR 자원'}
                </span>의 충전소 목록
              </h3>
              
                <>
                  {selectedDRResource || dashboardDataRef.current?.drResourceData?.Items?.[0] ? (
                    <>
                      <p className="station-list-title-count text-c2-400 text-gray-600 mb-5">
                        <b>{sidebarStationsAll.length}</b> 개의 데이터가 있습니다.
                      </p>
                      <div className="station-items">
                        {sidebarStationsPaginated.map((station) => (
                          <div
                            key={station.StationSysId}
                            id={`map-sidebar-station-${station.StationSysId}`}
                          >
                            <StationCard
                              station={station}
                              drResourceSysId={sidebarDrResourceSysIdForCards}
                              isSelected={
                                station.StationSysId === selectedSidebarStationSysId
                              }
                              onStationClick={handleStationClick}
                            />
                          </div>
                        ))}
                        {sidebarStationsAll.length === 0 && (
                          <div className="no-data">이 DR 자원에 연결된 충전소가 없습니다.</div>
                        )}
                      </div>
                      {sidebarStationsAll.length > STATION_SIDEBAR_PAGE_SIZE && (
                        <div
                          className="pagination-container"
                          style={{ marginTop: '16px', display: 'flex', justifyContent: 'center' }}
                        >
                          <Pagination
                            currentPage={stationSidebarPage}
                            totalPages={stationSidebarTotalPages}
                            onPageChange={setStationSidebarPage}
                            size="small"
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="loading">로딩 중...</div>
                  )}
                </>
              
              </div>
            )}
            <div className='my-10 flex justify-center px-20'>
              <div className='btn btn-primary big-btn flex-1 mx-10'
              onClick={() => {setShowChargerCompModal(true)}}
              >충전기 비교하기</div>
            </div>
        </div>
      </div>
      {showChargerCompModal && (
        <ChargerCompModal
          onClose={() => setShowChargerCompModal(false)}
        />
      )}

      
    </div>
  );
};

export default MapDashboard;